import React, { useState, useRef, lazy, Suspense, useEffect, useCallback } from 'react'
import { SwissPageProperties } from './SwissPageProperties'
const MediaLibraryModal = lazy(() => import('../MediaLibraryModal').then(m => ({ default: m.MediaLibraryModal })))
import { useEditorStore, useSelectedBlock } from '../../store/editor.store'
import type { BlockState, BlockTypography, BlockButton, BlockColors, BlockVisualStyles, SectionLayout } from '../../store/editor.store'
import { getBlock } from '@bst/blocks'
import { BASE } from '../../utils/api'
import type { FieldSchema } from '@bst/blocks'
import { abTestsApi, cmsApi, savedBlocksApi, ApiAbTest, translationsApi, aiTranslateApi, PageTranslation } from '../../utils/api'
import { api } from '../../utils/api'
import { DESIGN_STYLES } from '../../data/designStyles'

// ── Full-page text content cache ──────────────────────────────────────────────
const _pageTextCache = new Map<string, Record<string, string>>()
if (typeof window !== 'undefined') {
  window.addEventListener('bst:full-page-loaded', (e: Event) => {
    const { blockId, texts } = (e as CustomEvent<{ blockId: string; texts: Record<string, string> }>).detail
    _pageTextCache.set(blockId, texts)
  })
}


const FULL_PAGE_TYPES = [
  'swiss-page', 'glassmorphism-page',
  'swiss-landing-page', 'brutal-landing-page', 'serif-landing-page',
  'cinema-landing-page', 'maximal-landing-page', 'y2k-landing-page',
  'organic-landing-page', 'film-landing-page', 'bento-landing-page',
  'bento-portfolio-page', 'cinema-portfolio-page', 'film-portfolio-page',
  'minimal-portfolio-page', 'organic-portfolio-page',
  'swiss-portfolio-page', 'brutal-portfolio-page', 'immersive-portfolio-page',
  'neo-portfolio-page',
  'ai-studio-page',
]


const LANDING_STYLE_META: Record<string, [string, string]> = {
  'glassmorphism-page':   ['Modern SaaS',     'Glassmorphism лендинг — тёмный фон, размытое стекло, 4 цветовых бренда.'],
  'swiss-landing-page':   ['Swiss Landing',   'Швейцарский минимализм — чистая сетка, засечки, 4 бренда.'],
  'brutal-landing-page':  ['Brutal Landing',  'Нео-брутализм — жирные шрифты, точки, 4 яркие темы.'],
  'serif-landing-page':   ['Serif Landing',   'Элегантный сериф — 4 атмосферных вайба на одном шаблоне.'],
  'cinema-landing-page':  ['Cinema Landing',  'Кино-лендинг — cinematik эстетика и parallax-слои.'],
  'maximal-landing-page': ['Maximal Landing', 'Максимализм — bold типографика и перегруженный grid.'],
  'y2k-landing-page':     ['Y2K Landing',     'Ретро-футуризм Y2K — CRT, neon, Press Start 2P.'],
  'organic-landing-page': ['Organic Landing', 'Органический дизайн — природные текстуры, мягкие формы.'],
  'film-landing-page':    ['Film Landing',    'Кинопродакшн — letterbox-баннер, тёмные cinematic темы.'],
  'bento-landing-page':   ['Bento Landing',   'Bento-grid лендинг — модульная раскладка, 4 бренда.'],
}

const PORTFOLIO_META: Record<string, [string, string]> = {
  'bento-portfolio-page':     ['Bento Portfolio',    'Портфолио в стиле bento-grid — современный минимализм с тёмными карточками.'],
  'cinema-portfolio-page':    ['Cinema Portfolio',   'Кинематографическое портфолио — noir-эстетика с immersive-секциями.'],
  'film-portfolio-page':      ['Film Portfolio',     'Портфолио кинопродакшн — letterbox-эффект и тёмная тема.'],
  'minimal-portfolio-page':   ['Minimal Portfolio',  'Минималистичное портфолио — чистый acid/beton дизайн.'],
  'organic-portfolio-page':   ['Organic Portfolio',  'Органическое портфолио — природные текстуры и тёплая палитра.'],
  'swiss-portfolio-page':     ['Swiss Portfolio',    'Швейцарский стиль — студия айдентики и типографики.'],
  'brutal-portfolio-page':    ['Brutal Portfolio',   'Нео-брутализм — бюро громкого дизайна с Unbounded.'],
  'immersive-portfolio-page': ['Immersive Portfolio','Иммерсивное портфолио — noir/gold тема, Cormorant Garamond.'],
  'neo-portfolio-page':       ['Neo Portfolio',      'Нео-бенто портфолио — 4 темы: Noir, Atelier, Pulse, Garden.'],
}


export function PropertiesPanel() {
  const selection = useSelectedBlock()
  const { selectedSectionId, page, updateSection, updateBlock, setBlockAbTest, setBlockCustomCode } = useEditorStore()

  if (selection) {
    return <BlockProperties selection={selection} updateBlock={updateBlock} setBlockAbTest={setBlockAbTest} setBlockCustomCode={setBlockCustomCode} />
  }

  if (selectedSectionId) {
    const section = page.sections.find((s) => s.id === selectedSectionId)
    if (section) {
      return <SectionProperties
        paddingTop={section.paddingTop}
        paddingBottom={section.paddingBottom}
        background={section.background}
        backgroundSize={section.backgroundSize}
        backgroundPosition={section.backgroundPosition}
        backgroundVideo={section.backgroundVideo}
        bgOverlay={section.bgOverlay}
        bgOverlayOpacity={section.bgOverlayOpacity}
        fullHeight={section.fullHeight}
        minHeight={section.minHeight}
        sectionAnimation={section.sectionAnimation}
        anchorId={section.anchorId}
        sectionId={section.id}
        sectionName={section.name}
        sectionStyle={section.sectionStyle}
        sectionLayout={section.sectionLayout}
        onChange={(patch) => updateSection(selectedSectionId, patch)}
      />
    }
  }

  return <PageSettingsPanel />
}

function AbTestSection({ block, sectionId, setBlockAbTest }: {
  block: BlockState
  sectionId: string
  setBlockAbTest: (sectionId: string, blockId: string, abTestId: string, abVariant: 'A' | 'B' | '') => void
}) {
  const siteId = localStorage.getItem('bst-site-id')
  const [tests, setTests] = useState<ApiAbTest[]>([])

  useEffect(() => {
    if (!siteId) return
    abTestsApi.list(siteId).then(setTests).catch(() => setTests([]))
  }, [siteId])

  const currentTestId = block.abTestId ?? ''
  const currentVariant = block.abVariant ?? ''

  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>A/B тест</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <select
          className="prop-input"
          value={currentTestId}
          onChange={e => setBlockAbTest(sectionId, block.id, e.target.value, currentVariant as 'A' | 'B' | '')}
          style={{ fontSize: '12px' }}
        >
          <option value="">— Не участвует —</option>
          {tests.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {currentTestId && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {(['A', 'B'] as const).map(v => (
              <button
                key={v}
                onClick={() => setBlockAbTest(sectionId, block.id, currentTestId, v)}
                style={{
                  flex: 1, padding: '5px', border: `2px solid ${currentVariant === v ? 'var(--accent)' : 'var(--border)'}`,
                  background: currentVariant === v ? 'var(--accent)' : 'var(--bg-2)',
                  color: currentVariant === v ? '#fff' : 'var(--text-secondary)',
                  borderRadius: '6px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                Вариант {v}
              </button>
            ))}
          </div>
        )}
        {currentTestId && (
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            ID блока: <span style={{ fontFamily: 'monospace', userSelect: 'all' }}>{block.id}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function CustomCodeSection({ block, sectionId, setBlockCustomCode }: {
  block: BlockState
  sectionId: string
  setBlockCustomCode: (sectionId: string, blockId: string, css: string, js: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [css, setCss] = useState(block.customCss ?? '')
  const [js, setJs] = useState(block.customJs ?? '')
  const hasSomething = !!(block.customCss || block.customJs)

  if (!expanded) {
    return (
      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={() => setExpanded(true)}
          style={{ width: '100%', padding: '7px 10px', background: hasSomething ? 'var(--accent-light)' : 'var(--bg-3)', border: `1px solid ${hasSomething ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: hasSomething ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer', textAlign: 'left' }}>
          {`</>`} Custom CSS/JS {hasSomething ? '✓' : ''}
        </button>
      </div>
    )
  }

  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{`</>`} Custom CSS/JS</div>
        <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}>▲</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>CSS (применяется к #bst-{block.id.slice(0, 8)})</label>
          <textarea
            className="prop-textarea"
            rows={4}
            style={{ fontFamily: 'monospace', fontSize: '11px' }}
            placeholder={`.my-class {\n  color: red;\n}`}
            value={css}
            onChange={e => setCss(e.target.value)}
            onBlur={() => setBlockCustomCode(sectionId, block.id, css, js)}
          />
        </div>
        <div>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>JavaScript (выполняется после загрузки страницы)</label>
          <textarea
            className="prop-textarea"
            rows={4}
            style={{ fontFamily: 'monospace', fontSize: '11px' }}
            placeholder={`// document.getElementById('...')`}
            value={js}
            onChange={e => setJs(e.target.value)}
            onBlur={() => setBlockCustomCode(sectionId, block.id, css, js)}
          />
        </div>
      </div>
    </div>
  )
}

const ANIM_OPTIONS = [
  { value: '', label: 'Без анимации' },
  { value: 'fade-up', label: 'Fade Up ↑' },
  { value: 'fade-down', label: 'Fade Down ↓' },
  { value: 'fade-left', label: 'Fade Left ←' },
  { value: 'fade-right', label: 'Fade Right →' },
  { value: 'fade-in', label: 'Fade In' },
  { value: 'zoom-in', label: 'Zoom In' },
  { value: 'zoom-out', label: 'Zoom Out' },
  { value: 'flip-up', label: 'Flip Up' },
]

const DELAY_OPTIONS = [
  { value: 0, label: 'Без задержки' },
  { value: 100, label: '100 мс' },
  { value: 200, label: '200 мс' },
  { value: 300, label: '300 мс' },
  { value: 400, label: '400 мс' },
  { value: 500, label: '500 мс' },
  { value: 700, label: '700 мс' },
  { value: 1000, label: '1 сек' },
]

function AnimationSection({ block, sectionId, updateBlock }: {
  block: BlockState
  sectionId: string
  updateBlock: (sectionId: string, blockId: string, data: Record<string, unknown>) => void
}) {
  const animIn = String(block.data.animateIn ?? '')
  const animDelay = Number(block.data.animateDelay ?? 0)

  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>✨ Анимация при прокрутке</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div>
          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Эффект</label>
          <select
            className="prop-select"
            value={animIn}
            onChange={e => updateBlock(sectionId, block.id, { animateIn: e.target.value || undefined })}
          >
            {ANIM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {animIn && (
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Задержка</label>
            <select
              className="prop-select"
              value={animDelay}
              onChange={e => updateBlock(sectionId, block.id, { animateDelay: Number(e.target.value) || undefined })}
            >
              {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}

const HOVER_EFFECTS = [
  { value: '', label: '— нет —' },
  { value: 'lift', label: '⬆ Подъём (тень)' },
  { value: 'scale', label: '⊕ Увеличение' },
  { value: 'glow', label: '✨ Свечение' },
  { value: 'fade', label: '◐ Затемнение' },
  { value: 'shake', label: '↔ Встряска' },
]

const BUTTON_EFFECTS = [
  { value: '', label: '— Без эффекта' },
  { value: 'lift', label: '↑ Подъём' },
  { value: 'scale', label: '⊕ Увеличение' },
  { value: 'glow', label: '✦ Свечение' },
  { value: 'fill', label: '▶ Заливка' },
  { value: 'slide', label: '→ Слайд' },
]

function VisibilitySection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const { setBlockHideOn, setBlockHoverEffect, setBlockButtonEffect, setBlockWrapper } = useEditorStore()
  const hideOn = block.hideOn ?? []
  const toggle = (device: 'desktop' | 'tablet' | 'mobile') => {
    const next = hideOn.includes(device) ? hideOn.filter(d => d !== device) : [...hideOn, device]
    setBlockHideOn(sectionId, block.id, next)
  }
  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>👁 Видимость блока</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        {(['desktop', 'tablet', 'mobile'] as const).map(d => (
          <label
            key={d}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', border: `1px solid ${hideOn.includes(d) ? '#ef4444' : 'var(--border)'}`, background: hideOn.includes(d) ? '#fef2f2' : 'var(--bg-2)', color: hideOn.includes(d) ? '#ef4444' : 'var(--text-secondary)' }}
          >
            <input type="checkbox" checked={hideOn.includes(d)} onChange={() => toggle(d)} style={{ display: 'none' }} />
            {d === 'desktop' ? '🖥' : d === 'tablet' ? '📱' : '📲'} {d === 'desktop' ? 'ПК' : d === 'tablet' ? 'Планш.' : 'Моб.'}
          </label>
        ))}
      </div>
      {hideOn.length > 0 && (
        <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '5px' }}>
          Скрыт на: {hideOn.join(', ')}
        </div>
      )}
      <div style={{ marginTop: '10px' }}>
        <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>🖱 Hover-эффект</label>
        <select
          className="prop-select"
          value={block.hoverEffect ?? ''}
          onChange={e => setBlockHoverEffect(sectionId, block.id, e.target.value)}
        >
          {HOVER_EFFECTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ marginTop: '10px' }}>
        <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>🖱 Эффект кнопок</label>
        <select
          className="prop-select"
          value={block.buttonEffect ?? ''}
          onChange={e => setBlockButtonEffect(sectionId, block.id, e.target.value)}
        >
          {BUTTON_EFFECTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ marginTop: '10px' }}>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '5px' }}>📦 Отступы блока (px)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
          {(['wrapPaddingTop', 'wrapPaddingBottom', 'wrapPaddingLeft', 'wrapPaddingRight'] as const).map((k) => (
            <div key={k}>
              <label style={{ fontSize: '9px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
                {k === 'wrapPaddingTop' ? '↑ Сверху' : k === 'wrapPaddingBottom' ? '↓ Снизу' : k === 'wrapPaddingLeft' ? '← Слева' : '→ Справа'}
              </label>
              <input
                type="number"
                className="prop-input"
                value={block[k] ?? ''}
                min={0} max={200}
                placeholder="0"
                onChange={e => setBlockWrapper(sectionId, block.id, { [k]: e.target.value === '' ? undefined : Number(e.target.value) })}
                style={{ fontSize: '11px' }}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: '5px' }}>
          <label style={{ fontSize: '9px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>🎨 Фон блока</label>
          <input
            type="text"
            className="prop-input"
            value={block.wrapBackground ?? ''}
            placeholder="#ffffff или transparent"
            onChange={e => setBlockWrapper(sectionId, block.id, { wrapBackground: e.target.value || undefined })}
            style={{ fontSize: '11px' }}
          />
        </div>
      </div>
    </div>
  )
}

function BlockStylePicker({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const setBlockStyle = useEditorStore(s => s.setBlockStyle)
  const [open, setOpen] = useState(false)
  const current = block.blockStyle ?? ''

  return (
    <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', userSelect: 'none' }}
      >
        <span>🎨 Стиль блока</span>
        {current && <span style={{ fontSize: '10px', background: 'var(--accent)', color: '#fff', padding: '1px 6px', borderRadius: '100px', textTransform: 'none', letterSpacing: 0 }}>
          {DESIGN_STYLES.find(s => s.id === current)?.label ?? current}
        </span>}
        <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
          <button
            onClick={() => setBlockStyle(sectionId, block.id, '')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '8px 4px', borderRadius: '6px', cursor: 'pointer', border: !current ? '2px solid var(--accent)' : '1px solid var(--border)', background: !current ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)', fontSize: '10px', fontWeight: 600, color: 'var(--text-primary)' }}
          >
            <span style={{ fontSize: '14px' }}>○</span>
            <span>Нет</span>
          </button>
          {DESIGN_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => setBlockStyle(sectionId, block.id, style.id)}
              title={style.description}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '8px 4px', borderRadius: '6px', cursor: 'pointer', border: current === style.id ? '2px solid var(--accent)' : '1px solid var(--border)', background: current === style.id ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)' }}
            >
              <div style={{ width: '24px', height: '14px', borderRadius: '3px', background: style.previewBg, border: `2px solid ${style.previewAccent}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '8px', color: style.previewAccent }}>{style.emoji}</span>
              </div>
              <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.2 }}>{style.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Font families available in editor ─────────────────────────────────────────
const FONT_FAMILIES: { label: string; value: string; preview: string }[] = [
  { label: 'Системный', value: 'system-ui, -apple-system, sans-serif',  preview: 'Aa' },
  { label: 'Inter',     value: '"Inter", "Helvetica Neue", sans-serif',  preview: 'Aa' },
  { label: 'DM Sans',   value: '"DM Sans", "Helvetica Neue", sans-serif', preview: 'Aa' },
  { label: 'Playfair',  value: '"Playfair Display", Georgia, serif',     preview: 'Aa' },
  { label: 'Lora',      value: '"Lora", Georgia, serif',                 preview: 'Aa' },
  { label: 'Mono',      value: '"IBM Plex Mono", "Courier New", monospace', preview: 'Aa' },
  { label: 'Archivo',   value: '"Archivo Black", Impact, sans-serif',    preview: 'Aa' },
  { label: 'Caveat',    value: '"Caveat", cursive',                       preview: 'Aa' },
  { label: 'Orbitron',  value: '"Orbitron", monospace',                  preview: 'Aa' },
]

const FONT_WEIGHTS = ['300', '400', '500', '600', '700', '900']
const FONT_WEIGHT_LABELS: Record<string, string> = {
  '300': 'Thin', '400': 'Normal', '500': 'Medium', '600': 'Semi', '700': 'Bold', '900': 'Black'
}

function SectionHeader({ label, badge, open, onToggle }: { label: string; badge?: boolean; open: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px 10px', userSelect: 'none' }}
    >
      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      {badge && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />}
      <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
    </div>
  )
}

function TypographySection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const { setBlockTypography } = useEditorStore()
  const [open, setOpen] = useState(false)
  const typo = block.blockTypography ?? {}
  const hasChanges = !!(typo.fontFamily || typo.fontWeight || typo.textAlign || typo.letterSpacing || typo.lineHeight || typo.headingSize || typo.bodySize)

  function set(patch: Partial<BlockTypography>) {
    const next = { ...typo, ...patch }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockTypography(sectionId, block.id, hasAny ? next : undefined)
  }

  function clear(key: keyof BlockTypography) {
    const next = { ...typo, [key]: undefined }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockTypography(sectionId, block.id, hasAny ? next : undefined)
  }

  const btnBase: React.CSSProperties = { padding: '4px 6px', fontSize: '10px', cursor: 'pointer', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-primary)', lineHeight: 1.4 }
  const btnActive: React.CSSProperties = { ...btnBase, border: '2px solid var(--accent)', background: 'rgba(91,106,240,0.1)', color: 'var(--accent)', fontWeight: 700 }

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <SectionHeader label="Aa Типографика" badge={hasChanges} open={open} onToggle={() => setOpen(o => !o)} />
      {open && (
        <div style={{ padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Font family */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Шрифт</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
              <button
                onClick={() => setBlockTypography(sectionId, block.id, typo.fontFamily ? { ...typo, fontFamily: undefined } : undefined)}
                style={!typo.fontFamily ? btnActive : btnBase}
              >
                — Нет
              </button>
              {FONT_FAMILIES.map(f => (
                <button
                  key={f.value}
                  title={f.value}
                  onClick={() => set({ fontFamily: typo.fontFamily === f.value ? undefined : f.value })}
                  style={{ ...(typo.fontFamily === f.value ? btnActive : btnBase), fontFamily: f.value, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Font weight */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Жирность</label>
            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
              {FONT_WEIGHTS.map(w => (
                <button
                  key={w}
                  title={FONT_WEIGHT_LABELS[w]}
                  onClick={() => set({ fontWeight: typo.fontWeight === w ? undefined : w })}
                  style={{ ...(typo.fontWeight === w ? btnActive : btnBase), fontWeight: Number(w), flex: '1 0 auto' }}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          {/* Text align */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Выравнивание</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {([['left', '≡ Лево'], ['center', '≡ Центр'], ['right', '≡ Право']] as const).map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => set({ textAlign: typo.textAlign === v ? undefined : v })}
                  style={{ ...(typo.textAlign === v ? btnActive : btnBase), flex: 1 }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Heading size + Body size */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Размер заголовка</label>
              <select
                className="prop-select"
                value={typo.headingSize ?? ''}
                onChange={e => e.target.value ? set({ headingSize: e.target.value }) : clear('headingSize')}
                style={{ fontSize: '11px' }}
              >
                <option value="">— авто —</option>
                {['20px','24px','28px','32px','36px','42px','48px','56px','64px','72px','80px','96px'].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Размер текста</label>
              <select
                className="prop-select"
                value={typo.bodySize ?? ''}
                onChange={e => e.target.value ? set({ bodySize: e.target.value }) : clear('bodySize')}
                style={{ fontSize: '11px' }}
              >
                <option value="">— авто —</option>
                {['12px','13px','14px','15px','16px','17px','18px','20px','22px','24px'].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Letter spacing + Line height */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Межбукв.</label>
              <select
                className="prop-select"
                value={typo.letterSpacing ?? ''}
                onChange={e => e.target.value ? set({ letterSpacing: e.target.value }) : clear('letterSpacing')}
                style={{ fontSize: '11px' }}
              >
                <option value="">— нет —</option>
                {['-0.04em', '-0.02em', '0', '0.02em', '0.05em', '0.08em', '0.12em', '0.18em'].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Межстрочный</label>
              <select
                className="prop-select"
                value={typo.lineHeight ?? ''}
                onChange={e => e.target.value ? set({ lineHeight: e.target.value }) : clear('lineHeight')}
                style={{ fontSize: '11px' }}
              >
                <option value="">— нет —</option>
                {['1.0', '1.2', '1.4', '1.5', '1.6', '1.8', '2.0'].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {hasChanges && (
            <button
              onClick={() => setBlockTypography(sectionId, block.id, undefined)}
              style={{ fontSize: '10px', color: '#ef4444', background: 'none', border: '1px solid #ef4444', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', alignSelf: 'flex-start' }}
            >
              ✕ Сбросить всё
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const PRESET_COLORS = ['#5b6af0','#E4030E','#0E0E0E','#ffffff','#22c55e','#f59e0b','#06b6d4','#8b5cf6','#ec4899','#64748b']

function ButtonStyleSection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const { setBlockButton } = useEditorStore()
  const [open, setOpen] = useState(false)
  const btn = block.blockButton ?? {}
  const hasChanges = !!(btn.bg || btn.color || btn.radius !== undefined || btn.paddingSize || btn.borderColor)

  function set(patch: Partial<BlockButton>) {
    const next = { ...btn, ...patch }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockButton(sectionId, block.id, hasAny ? next : undefined)
  }

  function clear(key: keyof BlockButton) {
    const next = { ...btn, [key]: undefined }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockButton(sectionId, block.id, hasAny ? next : undefined)
  }

  const btnBase: React.CSSProperties = { padding: '5px 8px', fontSize: '11px', cursor: 'pointer', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-primary)', flex: 1 }
  const btnActive: React.CSSProperties = { ...btnBase, border: '2px solid var(--accent)', background: 'rgba(91,106,240,0.1)', color: 'var(--accent)', fontWeight: 700 }

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <SectionHeader label="⊡ Кнопки" badge={hasChanges} open={open} onToggle={() => setOpen(o => !o)} />
      {open && (
        <div style={{ padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Button background */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Цвет фона кнопки</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '5px' }}>
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  title={c}
                  onClick={() => set({ bg: btn.bg === c ? undefined : c })}
                  style={{ width: '22px', height: '22px', borderRadius: '4px', background: c, border: btn.bg === c ? '2px solid var(--accent)' : '1px solid var(--border)', cursor: 'pointer', outline: 'none', padding: 0 }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="color" value={btn.bg ?? '#5b6af0'} onChange={e => set({ bg: e.target.value })} style={{ width: '32px', height: '28px', padding: '2px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: 'none' }} />
              <input type="text" className="prop-input" value={btn.bg ?? ''} placeholder="#5b6af0" onChange={e => e.target.value ? set({ bg: e.target.value }) : clear('bg')} style={{ fontSize: '11px', flex: 1 }} />
              {btn.bg && <button onClick={() => clear('bg')} style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
            </div>
          </div>

          {/* Button text color */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Цвет текста кнопки</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '5px' }}>
              {PRESET_COLORS.map(c => (
                <button key={c} title={c} onClick={() => set({ color: btn.color === c ? undefined : c })} style={{ width: '22px', height: '22px', borderRadius: '4px', background: c, border: btn.color === c ? '2px solid var(--accent)' : '1px solid var(--border)', cursor: 'pointer', padding: 0 }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="color" value={btn.color ?? '#ffffff'} onChange={e => set({ color: e.target.value })} style={{ width: '32px', height: '28px', padding: '2px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: 'none' }} />
              <input type="text" className="prop-input" value={btn.color ?? ''} placeholder="#ffffff" onChange={e => e.target.value ? set({ color: e.target.value }) : clear('color')} style={{ fontSize: '11px', flex: 1 }} />
              {btn.color && <button onClick={() => clear('color')} style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
            </div>
          </div>

          {/* Border radius */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Скругление: <b>{btn.radius ?? 8}px</b></label>
            <input
              type="range" min={0} max={50} step={1}
              value={btn.radius ?? 8}
              onChange={e => set({ radius: Number(e.target.value) })}
              style={{ width: '100%', accentColor: 'var(--accent)' }}
            />
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {[[0,'Острые'],[4,'Малый'],[8,'Стандарт'],[16,'Большой'],[50,'Круглые']].map(([v,l]) => (
                <button key={v} onClick={() => set({ radius: Number(v) })} style={{ ...(btn.radius === Number(v) ? btnActive : btnBase), padding: '3px 5px', fontSize: '9px', flex: 'none' }}>{l as string}</button>
              ))}
            </div>
          </div>

          {/* Button size */}
          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Размер кнопок</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {([['', 'Авто'], ['sm', 'S — мал.'], ['md', 'M — станд.'], ['lg', 'L — крупн.']] as const).map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => set({ paddingSize: v || undefined })}
                  style={btn.paddingSize === v || (!btn.paddingSize && !v) ? btnActive : btnBase}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {hasChanges && (
            <button
              onClick={() => setBlockButton(sectionId, block.id, undefined)}
              style={{ fontSize: '10px', color: '#ef4444', background: 'none', border: '1px solid #ef4444', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', alignSelf: 'flex-start' }}
            >
              ✕ Сбросить кнопки
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ColorsSection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const { setBlockColors, setBlockWrapper } = useEditorStore()
  const [open, setOpen] = useState(false)
  const colors = block.blockColors ?? {}
  const hasChanges = !!(colors.bg || colors.textColor || colors.accentColor)

  function set(patch: Partial<BlockColors>) {
    const next = { ...colors, ...patch }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockColors(sectionId, block.id, hasAny ? next : undefined)
  }

  function clearColor(key: keyof BlockColors) {
    const next = { ...colors, [key]: undefined }
    const hasAny = Object.values(next).some(v => v !== undefined && v !== '')
    setBlockColors(sectionId, block.id, hasAny ? next : undefined)
  }

  const colorRows: { key: keyof BlockColors; label: string; placeholder: string; varName: string }[] = [
    { key: 'bg',          label: 'Фон блока',    placeholder: '#ffffff',  varName: '--bst-bg-override' },
    { key: 'textColor',   label: 'Цвет текста',  placeholder: '#0e0e0e',  varName: '--bst-color-override' },
    { key: 'accentColor', label: 'Акцент / CTA', placeholder: '#5b6af0',  varName: '--bst-accent-override' },
  ]

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <SectionHeader label="🎨 Цвета" badge={hasChanges} open={open} onToggle={() => setOpen(o => !o)} />
      {open && (
        <div style={{ padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {colorRows.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{label}</label>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '5px' }}>
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    title={c}
                    onClick={() => set({ [key]: colors[key] === c ? undefined : c })}
                    style={{ width: '22px', height: '22px', borderRadius: '4px', background: c, border: colors[key] === c ? '2px solid var(--accent)' : '1px solid var(--border)', cursor: 'pointer', padding: 0 }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={colors[key] ?? placeholder}
                  onChange={e => set({ [key]: e.target.value })}
                  style={{ width: '32px', height: '28px', padding: '2px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: 'none' }}
                />
                <input
                  type="text"
                  className="prop-input"
                  value={colors[key] ?? ''}
                  placeholder={placeholder}
                  onChange={e => e.target.value ? set({ [key]: e.target.value }) : clearColor(key)}
                  style={{ fontSize: '11px', flex: 1 }}
                />
                {colors[key] && (
                  <button onClick={() => clearColor(key)} style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                )}
              </div>
            </div>
          ))}

          <div>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Мин. высота блока (px)</label>
            <input
              type="number"
              className="prop-input"
              value={block.blockMinHeight ?? ''}
              min={0} max={2000} step={10}
              placeholder="авто"
              onChange={e => setBlockWrapper(sectionId, block.id, { blockMinHeight: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={{ fontSize: '11px' }}
            />
          </div>

          {hasChanges && (
            <button
              onClick={() => setBlockColors(sectionId, block.id, undefined)}
              style={{ fontSize: '10px', color: '#ef4444', background: 'none', border: '1px solid #ef4444', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', alignSelf: 'flex-start' }}
            >
              ✕ Сбросить цвета
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Full-page text editor helpers ────────────────────────────────────────────
const fpInp = {
  width: '100%', padding: '6px 8px', fontSize: '12px',
  border: '1px solid var(--border)', borderRadius: '6px',
  background: 'var(--bg-2)', color: 'var(--text-primary)',
  fontFamily: 'inherit',
} as const

const fpLabelStyle = {
  fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block',
} as const

const fpSectionHead = {
  fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)',
  textTransform: 'uppercase' as const, letterSpacing: '0.08em',
  padding: '10px 12px 4px', borderTop: '1px solid var(--border)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  cursor: 'pointer',
}

function FPAccordion({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={fpSectionHead} onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        <span style={{ opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {children}
        </div>
      )}
    </div>
  )
}

function FPField({ label: lbl, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={fpLabelStyle}>{lbl}</span>
      {children}
    </div>
  )
}

// ─── Static field definitions per template ────────────────────────────────────
interface FPFieldDef {
  id: string
  label: string
  section: string
  multiline?: true
}

const TEMPLATE_FIELDS: Record<string, FPFieldDef[]> = {
  'glassmorphism-page': [
    { id: 'heroBadge',  label: 'Бейдж',               section: '🦸 Герой' },
    { id: 'heroH1',     label: 'Заголовок H1',          section: '🦸 Герой', multiline: true },
    { id: 'heroSub',    label: 'Подзаголовок',          section: '🦸 Герой', multiline: true },
    { id: 'heroWord',   label: 'Фоновое слово',          section: '🦸 Герой' },
    { id: 'featH2',     label: 'Заголовок',             section: '⚡ Возможности', multiline: true },
    { id: 'featP',      label: 'Описание',              section: '⚡ Возможности', multiline: true },
    { id: 'howH2',      label: 'Заголовок',             section: '🔢 Как работает', multiline: true },
    { id: 'reviewsH2',  label: 'Заголовок',             section: '💬 Отзывы', multiline: true },
    { id: 'priceH2',    label: 'Заголовок',             section: '💳 Тарифы', multiline: true },
    { id: 'priceP',     label: 'Подзаголовок',          section: '💳 Тарифы', multiline: true },
    { id: 'faqH2',      label: 'Заголовок',             section: '❓ FAQ', multiline: true },
    { id: 'ctaH2',      label: 'Заголовок призыва',     section: '📣 CTA', multiline: true },
    { id: 'ctaP',       label: 'Описание',              section: '📣 CTA', multiline: true },
    { id: 'footerDesc', label: 'Описание компании',     section: '🔻 Футер', multiline: true },
  ],
  'swiss-landing-page': [
    { id: 'wordmark',    label: 'Логотип',              section: '🏢 Бренд' },
    { id: 'brandName',   label: 'Название',             section: '🏢 Бренд' },
    { id: 'brandFull',   label: 'Полное название',      section: '🏢 Бренд' },
    { id: 'heroCity',    label: 'Город',                section: '🏢 Бренд' },
    { id: 'heroKicker',  label: 'Кикер',                section: '🦸 Герой' },
    { id: 'hl0',         label: 'Заголовок — стр. 1',  section: '🦸 Герой' },
    { id: 'hl1',         label: 'Заголовок — стр. 2',  section: '🦸 Герой' },
    { id: 'hl2',         label: 'Заголовок — стр. 3',  section: '🦸 Герой' },
    { id: 'heroTagline', label: 'Описание',             section: '🦸 Герой', multiline: true },
    { id: 'heroCap',     label: 'Подпись изображения', section: '🦸 Герой' },
    { id: 'manifestNum', label: 'Год основания',        section: '📖 Манифест' },
    { id: 'manifestIdx', label: 'Индекс',              section: '📖 Манифест' },
    { id: 'man1',        label: 'Манифест — абз. 1',   section: '📖 Манифест', multiline: true },
    { id: 'man2',        label: 'Манифест — абз. 2',   section: '📖 Манифест', multiline: true },
    { id: 'worksIdx',    label: 'Работы — индекс',     section: '🎬 Работы' },
    { id: 'quoteText',   label: 'Цитата',              section: '💬 Цитата', multiline: true },
    { id: 'quoteAuthor', label: 'Автор',               section: '💬 Цитата' },
    { id: 'email',       label: 'Email',                section: '📬 Контакт' },
    { id: 'contactNote', label: 'Описание контакта',   section: '📬 Контакт', multiline: true },
    { id: 'ctaBtn',      label: 'Кнопка CTA',          section: '📬 Контакт' },
  ],
  'brutal-landing-page': [
    { id: 'htKicker',    label: 'Кикер',                section: '🦸 Герой' },
    { id: 'htWord',      label: 'Главное слово',        section: '🦸 Герой' },
    { id: 'htTag',       label: 'Описание',             section: '🦸 Герой', multiline: true },
    { id: 'st1',         label: 'Подзаголовок 1',       section: '🦸 Герой' },
    { id: 'st2',         label: 'Подзаголовок 2',       section: '🦸 Герой' },
  ],
  'serif-landing-page': [
    { id: 'logo',        label: 'Логотип',              section: '🏢 Бренд' },
    { id: 'themeName',   label: 'Название темы',        section: '🏢 Бренд' },
    { id: 'footTheme',   label: 'Вариант (футер)',      section: '🏢 Бренд' },
    { id: 'heroKicker',  label: 'Кикер',                section: '🦸 Герой' },
    { id: 'heroTitle',   label: 'Заголовок',            section: '🦸 Герой', multiline: true },
    { id: 'heroLead',    label: 'Описание',             section: '🦸 Герой', multiline: true },
    { id: 'heroCap',     label: 'Подпись',              section: '🦸 Герой' },
  ],
  'cinema-landing-page': [
    { id: 'logo',        label: 'Логотип',              section: '🏢 Бренд' },
    { id: 'themeName',   label: 'Название темы',        section: '🏢 Бренд' },
    { id: 'heroKicker',  label: 'Кикер',                section: '🦸 Герой' },
    { id: 'heroTitle',   label: 'Заголовок',            section: '🦸 Герой', multiline: true },
    { id: 'heroLead',    label: 'Описание',             section: '🦸 Герой', multiline: true },
    { id: 'heroCap',     label: 'Подпись',              section: '🦸 Герой' },
  ],
  'maximal-landing-page': [
    { id: 'htKicker',    label: 'Кикер',                section: '🦸 Герой' },
    { id: 'htWord',      label: 'Главное слово',        section: '🦸 Герой' },
    { id: 'htTag',       label: 'Описание',             section: '🦸 Герой', multiline: true },
    { id: 'st1',         label: 'Подзаголовок 1',       section: '🦸 Герой' },
    { id: 'st2',         label: 'Подзаголовок 2',       section: '🦸 Герой' },
  ],
  'y2k-landing-page': [
    { id: 'heroTitle',   label: 'Логотип / бренд',      section: '🏢 Бренд' },
    { id: 'vName',       label: 'Режим (хедер)',         section: '🏢 Бренд' },
    { id: 'vNameFoot',   label: 'Режим (футер)',         section: '🏢 Бренд' },
    { id: 'heroTag',     label: 'Подзаголовок',          section: '🦸 Герой' },
    { id: 'heroSub',     label: 'Описание',              section: '🦸 Герой', multiline: true },
    { id: 'sendBtn',     label: 'Кнопка формы',          section: '📬 Контакт' },
    { id: 'formOk',      label: 'Успех формы',           section: '📬 Контакт', multiline: true },
  ],
  'film-landing-page': [
    { id: 'hdrVar',      label: 'Вариант (хедер)',       section: '🏢 Бренд' },
    { id: 'kicker',      label: 'Кикер',                 section: '🦸 Герой' },
    { id: 'tl0',         label: 'Заголовок — стр. 1',   section: '🦸 Герой' },
    { id: 'tl1',         label: 'Заголовок — стр. 2',   section: '🦸 Герой' },
    { id: 'tl2',         label: 'Заголовок — стр. 3',   section: '🦸 Герой' },
    { id: 'heroSub',     label: 'Описание',              section: '🦸 Герой', multiline: true },
    { id: 'cta1',        label: 'Кнопка 1',              section: '📬 Контакт' },
    { id: 'cta2',        label: 'Кнопка 2',              section: '📬 Контакт' },
    { id: 'wkT1',        label: 'Проект 1 — название',  section: '🎬 Работы' },
    { id: 'wkM1',        label: 'Проект 1 — категория', section: '🎬 Работы' },
    { id: 'wkY1',        label: 'Проект 1 — год',       section: '🎬 Работы' },
    { id: 'wkT2',        label: 'Проект 2 — название',  section: '🎬 Работы' },
    { id: 'wkM2',        label: 'Проект 2 — категория', section: '🎬 Работы' },
    { id: 'wkY2',        label: 'Проект 2 — год',       section: '🎬 Работы' },
    { id: 'wkT3',        label: 'Проект 3 — название',  section: '🎬 Работы' },
    { id: 'wkM3',        label: 'Проект 3 — категория', section: '🎬 Работы' },
    { id: 'wkY3',        label: 'Проект 3 — год',       section: '🎬 Работы' },
    { id: 'wkT4',        label: 'Проект 4 — название',  section: '🎬 Работы' },
    { id: 'wkM4',        label: 'Проект 4 — категория', section: '🎬 Работы' },
    { id: 'wkY4',        label: 'Проект 4 — год',       section: '🎬 Работы' },
    { id: 'studioLead',  label: 'О студии',              section: '📖 Манифест', multiline: true },
    { id: 'quote',       label: 'Цитата',                section: '💬 Цитата', multiline: true },
    { id: 'quoteSrc',    label: 'Автор цитаты',          section: '💬 Цитата' },
    { id: 'mani0',       label: 'Манифест — ч. 1',      section: '📖 Манифест' },
    { id: 'mani1',       label: 'Манифест — ч. 2',      section: '📖 Манифест' },
    { id: 'mani2',       label: 'Манифест — ч. 3',      section: '📖 Манифест' },
  ],
  'organic-landing-page': [
    { id: 'brandSuffix', label: 'Суффикс бренда',       section: '🏢 Бренд' },
    { id: 'heroEyebrow', label: 'Надзаголовок',          section: '🦸 Герой' },
    { id: 'heroTitle',   label: 'Заголовок',             section: '🦸 Герой', multiline: true },
    { id: 'heroLead',    label: 'Описание',              section: '🦸 Герой', multiline: true },
    { id: 'heroBadge',   label: 'Бейдж',                section: '🦸 Герой' },
    { id: 'heroBtn',     label: 'Кнопка',               section: '🦸 Герой' },
    { id: 'heroLink',    label: 'Ссылка-текст',          section: '🦸 Герой' },
    { id: 'manEyebrow',  label: 'О нас — надзаголовок', section: '📖 Манифест' },
    { id: 'manText',     label: 'О нас — текст',         section: '📖 Манифест', multiline: true },
    { id: 'apEyebrow',   label: 'Подход — надзаголовок', section: '📖 Манифест' },
    { id: 'apTitle',     label: 'Подход — заголовок',   section: '📖 Манифест' },
    { id: 'apNote',      label: 'Подход — описание',    section: '📖 Манифест', multiline: true },
    { id: 'wkEyebrow',   label: 'Работы — надзаголовок', section: '🎬 Работы' },
    { id: 'wkTitle',     label: 'Работы — заголовок',   section: '🎬 Работы' },
    { id: 'wkSub',       label: 'Работы — подзаголовок', section: '🎬 Работы' },
    { id: 'qText',       label: 'Цитата',               section: '💬 Цитата', multiline: true },
    { id: 'qAuthor',     label: 'Автор',                section: '💬 Цитата' },
    { id: 'ctaTitle',    label: 'CTA — заголовок',      section: '📬 Контакт', multiline: true },
    { id: 'ctaBtn',      label: 'CTA — кнопка',         section: '📬 Контакт' },
    { id: 'ctaNote',     label: 'CTA — примечание',     section: '📬 Контакт' },
  ],
  'bento-landing-page': [
    { id: 'title',       label: 'Заголовок',             section: '🦸 Герой', multiline: true },
    { id: 'sub',         label: 'Описание',              section: '🦸 Герой', multiline: true },
    { id: 'pill',        label: 'Метка',                 section: '🦸 Герой' },
    { id: 'btn1',        label: 'Кнопка 1 — текст',     section: '🦸 Герой' },
    { id: 'btn1__href',  label: 'Кнопка 1 — ссылка',   section: '🦸 Герой' },
    { id: 'btn2',        label: 'Кнопка 2 — текст',     section: '🦸 Герой' },
    { id: 'btn2__href',  label: 'Кнопка 2 — ссылка',   section: '🦸 Герой' },
    { id: 'hcta',        label: 'Header CTA — текст',   section: '🦸 Герой' },
    { id: 'hcta__href',  label: 'Header CTA — ссылка', section: '🦸 Герой' },
    { id: 'badge',       label: 'Бейдж',                section: '🦸 Герой' },
    { id: 'cap',         label: 'Подпись фото',          section: '🦸 Герой' },
    { id: 'meta',        label: 'Мета строка',           section: '🦸 Герой' },
    { id: 'quote',       label: 'Цитата',               section: '💬 Цитата', multiline: true },
    { id: 'quoteA',      label: 'Автор',                section: '💬 Цитата' },
    { id: 'quoteR',      label: 'Роль автора',          section: '💬 Цитата' },
    { id: 'mani',        label: 'Манифест',              section: '📖 Манифест', multiline: true },
    { id: 'ctaT',        label: 'CTA — заголовок',      section: '📬 Контакт', multiline: true },
    { id: 'ctaX',        label: 'CTA — текст',          section: '📬 Контакт', multiline: true },
    { id: 'ctaB',        label: 'CTA — кнопка текст',   section: '📬 Контакт' },
    { id: 'ctaB__href',  label: 'CTA — кнопка ссылка', section: '📬 Контакт' },
    { id: 'ctaN',        label: 'CTA — примечание',     section: '📬 Контакт' },
    { id: 'listT',       label: 'Список — заголовок',   section: '🛠 Услуги' },
    { id: 'list1',       label: 'Пункт 1',              section: '🛠 Услуги' },
    { id: 'list2',       label: 'Пункт 2',              section: '🛠 Услуги' },
    { id: 'list3',       label: 'Пункт 3',              section: '🛠 Услуги' },
    { id: 'specT',       label: 'Специализация',        section: '🛠 Услуги' },
    { id: 'marq',        label: 'Бегущая строка (через запятую)', section: '📝 Прочее' },
    { id: 'city',        label: 'Город 1',              section: '📝 Прочее' },
    { id: 'city2',       label: 'Город 2',              section: '📝 Прочее' },
    { id: 'coords',      label: 'Координаты',           section: '📝 Прочее' },
    { id: '__fd__',      label: 'Шрифт заголовков (Google Font)', section: '🎨 Шрифты' },
    { id: '__fb__',      label: 'Шрифт текста (Google Font)', section: '🎨 Шрифты' },
  ],

  // ─── Portfolio ────────────────────────────────────────────────────────────────
  'cinema-portfolio-page': [
    { id: 'heroKicker',    label: 'Кикер',               section: '🦸 Герой' },
    { id: 'heroTitle',     label: 'Заголовок',           section: '🦸 Герой', multiline: true },
    { id: 'heroSub',       label: 'Описание',            section: '🦸 Герой', multiline: true },
    { id: 'heroMeta',      label: 'Мета',                section: '🦸 Герой' },
    { id: 'worksTitle',    label: 'Работы — заголовок', section: '🎬 Работы' },
    { id: 'worksIntro',    label: 'Работы — вступление', section: '🎬 Работы', multiline: true },
    { id: 'p1Tag',         label: 'Проект 1 — тег',     section: '🎬 Работы' },
    { id: 'p1Title',       label: 'Проект 1 — название', section: '🎬 Работы' },
    { id: 'p1Year',        label: 'Проект 1 — год',     section: '🎬 Работы' },
    { id: 'p1Desc',        label: 'Проект 1 — описание', section: '🎬 Работы', multiline: true },
    { id: 'p2Tag',         label: 'Проект 2 — тег',     section: '🎬 Работы' },
    { id: 'p2Title',       label: 'Проект 2 — название', section: '🎬 Работы' },
    { id: 'p2Year',        label: 'Проект 2 — год',     section: '🎬 Работы' },
    { id: 'p2Desc',        label: 'Проект 2 — описание', section: '🎬 Работы', multiline: true },
    { id: 'p3Tag',         label: 'Проект 3 — тег',     section: '🎬 Работы' },
    { id: 'p3Title',       label: 'Проект 3 — название', section: '🎬 Работы' },
    { id: 'p3Year',        label: 'Проект 3 — год',     section: '🎬 Работы' },
    { id: 'p3Desc',        label: 'Проект 3 — описание', section: '🎬 Работы', multiline: true },
    { id: 'aboutTitle',    label: 'О нас — заголовок',  section: '📖 Манифест' },
    { id: 'aboutLead',     label: 'О нас — лид',        section: '📖 Манифест', multiline: true },
    { id: 'aboutText',     label: 'О нас — текст',      section: '📖 Манифест', multiline: true },
    { id: 'servicesTitle', label: 'Услуги — заголовок', section: '🛠 Услуги' },
    { id: 's1Title',       label: 'Услуга 1 — название', section: '🛠 Услуги' },
    { id: 's1Desc',        label: 'Услуга 1 — описание', section: '🛠 Услуги', multiline: true },
    { id: 's2Title',       label: 'Услуга 2 — название', section: '🛠 Услуги' },
    { id: 's2Desc',        label: 'Услуга 2 — описание', section: '🛠 Услуги', multiline: true },
    { id: 's3Title',       label: 'Услуга 3 — название', section: '🛠 Услуги' },
    { id: 's3Desc',        label: 'Услуга 3 — описание', section: '🛠 Услуги', multiline: true },
    { id: 's4Title',       label: 'Услуга 4 — название', section: '🛠 Услуги' },
    { id: 's4Desc',        label: 'Услуга 4 — описание', section: '🛠 Услуги', multiline: true },
    { id: 'ctaTitle',      label: 'CTA — заголовок',    section: '📬 Контакт', multiline: true },
    { id: 'ctaText',       label: 'CTA — текст',        section: '📬 Контакт', multiline: true },
    { id: 'footDesc',      label: 'Футер — описание',   section: '📬 Контакт', multiline: true },
  ],
  'swiss-portfolio-page': [
    { id: 'bstTitle',      label: 'Логотип',            section: '🏢 Бренд' },
    { id: 'variantLabel',  label: 'Вариант',            section: '🏢 Бренд' },
    { id: 'tagline',       label: 'Слоган',             section: '🏢 Бренд', multiline: true },
    { id: 'lede',          label: 'Описание стиля',     section: '🦸 Герой', multiline: true },
    { id: 'featuredTitle', label: 'Избр. проект — название', section: '🎬 Работы' },
    { id: 'featuredMeta',  label: 'Избр. проект — мета', section: '🎬 Работы' },
    { id: 'themeFoot',     label: 'Футер — тема',       section: '📝 Прочее' },
  ],
  'brutal-portfolio-page': [
    { id: 'heroWord',      label: 'Главное слово',      section: '🦸 Герой' },
    { id: 'heroSub',       label: 'Описание',           section: '🦸 Герой', multiline: true },
    { id: 'heroBrand',     label: 'Тема (хиро)',        section: '🏢 Бренд' },
    { id: 'ppName',        label: 'Вариант — название', section: '🏢 Бренд' },
    { id: 'ppTag',         label: 'Вариант — тег',     section: '🏢 Бренд' },
    { id: 'ppSub',         label: 'Вариант — описание', section: '🏢 Бренд', multiline: true },
    { id: 'footBrand',     label: 'Футер — бренд',     section: '📝 Прочее' },
  ],
  'minimal-portfolio-page': [
    { id: 'brandName',     label: 'Бренд — название',  section: '🏢 Бренд' },
    { id: 'modeName',      label: 'Режим',             section: '🏢 Бренд' },
    { id: 'heroTitle',     label: 'Заголовок',         section: '🦸 Герой' },
    { id: 'heroSub',       label: 'Описание',          section: '🦸 Герой', multiline: true },
    { id: 'quoteText',     label: 'Цитата',            section: '💬 Цитата', multiline: true },
    { id: 'quoteAuthor',   label: 'Автор',             section: '💬 Цитата' },
    { id: 'ctaText',       label: 'Кнопка CTA',        section: '📬 Контакт' },
  ],
  'immersive-portfolio-page': [
    { id: 'themeLabel',    label: 'Тема',              section: '🏢 Бренд' },
    { id: 'sceneLabel',    label: 'Сцена',             section: '🦸 Герой' },
    { id: 'heroTitle',     label: 'Заголовок',         section: '🦸 Герой' },
    { id: 'heroSub',       label: 'Описание',          section: '🦸 Герой', multiline: true },
  ],
  'neo-portfolio-page': [
    { id: 'themeName',     label: 'Тема',              section: '🏢 Бренд' },
    { id: 'tagline',       label: 'Слоган',            section: '🦸 Герой', multiline: true },
    { id: 'focusTxt',      label: 'Фокус-проект',      section: '🎬 Работы' },
    { id: 'tstQ',          label: 'Отзыв — цитата',    section: '💬 Цитата', multiline: true },
    { id: 'tstA',          label: 'Отзыв — автор',     section: '💬 Цитата' },
  ],
  'film-portfolio-page': [
    { id: 'heroPill',      label: 'Бейдж доступности', section: '🦸 Герой' },
    { id: 'heroEyebrow',   label: 'Надзаголовок',      section: '🦸 Герой' },
    { id: 'heroTitle',     label: 'Заголовок',         section: '🦸 Герой', multiline: true },
    { id: 'heroSub',       label: 'Описание',          section: '🦸 Герой', multiline: true },
    { id: 'heroCta1',      label: 'Кнопка 1',          section: '🦸 Герой' },
    { id: 'heroCta2',      label: 'Кнопка 2',          section: '🦸 Герой' },
    { id: 'studioTitle',   label: 'Студия — заголовок', section: '📖 Манифест' },
    { id: 'studioNote',    label: 'Студия — подзаголовок', section: '📖 Манифест' },
    { id: 'studioText',    label: 'Студия — текст',    section: '📖 Манифест', multiline: true },
    { id: 'studioBrand',   label: 'Студия — манифест', section: '📖 Манифест', multiline: true },
    { id: 'worksTitle',    label: 'Работы — заголовок', section: '🎬 Работы' },
    { id: 'worksNote',     label: 'Работы — описание', section: '🎬 Работы' },
    { id: 'ctaKicker',     label: 'CTA — кикер',       section: '📬 Контакт' },
    { id: 'ctaTitle',      label: 'CTA — заголовок',   section: '📬 Контакт', multiline: true },
    { id: 'ctaNote',       label: 'CTA — подпись',     section: '📬 Контакт' },
    { id: 'ctaEmail',      label: 'Email',              section: '📬 Контакт' },
  ],
  'bento-portfolio-page': [
    { id: 'heroKicker',    label: 'Кикер',             section: '🦸 Герой' },
    { id: 'heroWord',      label: 'Ключевое слово',    section: '🦸 Герой' },
    { id: 'heroSub',       label: 'Описание',          section: '🦸 Герой', multiline: true },
    { id: 'heroCta1',      label: 'Кнопка 1',          section: '🦸 Герой' },
    { id: 'heroCta2',      label: 'Кнопка 2',          section: '🦸 Герой' },
    { id: 'studioText1',   label: 'О студии — ч. 1',  section: '📖 Манифест', multiline: true },
    { id: 'studioText2',   label: 'О студии — ч. 2',  section: '📖 Манифест', multiline: true },
    { id: 'studioText3',   label: 'О студии — ч. 3',  section: '📖 Манифест', multiline: true },
    { id: 'ctaKicker',     label: 'CTA — кикер',       section: '📬 Контакт' },
    { id: 'ctaTitle',      label: 'CTA — заголовок',   section: '📬 Контакт', multiline: true },
    { id: 'ctaEmail',      label: 'Email',              section: '📬 Контакт' },
  ],
  'organic-portfolio-page': [
    { id: 'heroKicker',    label: 'Надзаголовок',      section: '🏢 Бренд' },
    { id: 'brandTitle',    label: 'Название бренда',   section: '🏢 Бренд' },
    { id: 'heroCoords',    label: 'Координаты',        section: '🏢 Бренд' },
    { id: 'tagline0',      label: 'Слоган — стр. 1',  section: '🦸 Герой' },
    { id: 'tagline1',      label: 'Слоган — стр. 2',  section: '🦸 Герой' },
    { id: 'tagline2',      label: 'Слоган — стр. 3',  section: '🦸 Герой' },
    { id: 'lede',          label: 'Описание стиля',    section: '🦸 Герой', multiline: true },
    { id: 'studioText',    label: 'О студии — текст',  section: '📖 Манифест', multiline: true },
    { id: 'studioNote',    label: 'О студии — примеч.', section: '📖 Манифест', multiline: true },
    { id: 'ctaTitle',      label: 'CTA — заголовок',   section: '📬 Контакт', multiline: true },
  ],
}

function FullPageContentEditor({ block, sectionId, updateBlock }: {
  block: BlockState
  sectionId: string
  updateBlock: (sectionId: string, blockId: string, data: Record<string, unknown>) => void
}) {
  const [scannedTexts, setScannedTexts] = useState<Record<string, string>>(
    () => _pageTextCache.get(block.id) ?? {}
  )

  useEffect(() => {
    const cached = _pageTextCache.get(block.id)
    if (cached && Object.keys(cached).length > 0) setScannedTexts(cached)
    const handler = (e: Event) => {
      const { blockId, texts } = (e as CustomEvent<{ blockId: string; texts: Record<string, string> }>).detail
      if (blockId === block.id) setScannedTexts(texts)
    }
    window.addEventListener('bst:full-page-loaded', handler)
    return () => window.removeEventListener('bst:full-page-loaded', handler)
  }, [block.id])

  const fields = TEMPLATE_FIELDS[block.type] ?? []
  const overrides = (block.data.textOverrides ?? {}) as Record<string, string>
  const hasOverrides = Object.keys(overrides).length > 0

  function setOverride(id: string, value: string) {
    const next = { ...overrides }
    if (value.trim()) next[id] = value
    else delete next[id]
    updateBlock(sectionId, block.id, { ...block.data, textOverrides: next })
  }

  function renderField(f: FPFieldDef) {
    const placeholder = scannedTexts[f.id] ?? ''
    const currentValue = overrides[f.id] ?? ''
    return (
      <FPField key={f.id} label={f.label}>
        {f.multiline ? (
          <textarea
            style={{ ...fpInp, resize: 'vertical', minHeight: '64px' }}
            value={currentValue}
            placeholder={placeholder.substring(0, 120) + (placeholder.length > 120 ? '…' : '')}
            onChange={e => setOverride(f.id, e.target.value)}
          />
        ) : (
          <input
            style={fpInp}
            value={currentValue}
            placeholder={placeholder}
            onChange={e => setOverride(f.id, e.target.value)}
          />
        )}
      </FPField>
    )
  }

  if (fields.length === 0) {
    return (
      <div style={{ padding: '12px', fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', borderTop: '2px solid var(--border)' }}>
        ✏ Текст встроен в шаблон и не редактируется в этой версии.
      </div>
    )
  }

  // Group fields by section, preserving order
  const sectionMap = new Map<string, FPFieldDef[]>()
  for (const f of fields) {
    if (!sectionMap.has(f.section)) sectionMap.set(f.section, [])
    sectionMap.get(f.section)!.push(f)
  }

  return (
    <div style={{ borderTop: '2px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px 6px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: hasOverrides ? 'var(--accent)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          ✏ Текст страницы{hasOverrides ? ' ✓' : ''}
        </span>
        {hasOverrides && (
          <button
            style={{ padding: '3px 8px', fontSize: '10px', background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-muted)' }}
            onClick={() => updateBlock(sectionId, block.id, { ...block.data, textOverrides: {} })}
          >
            ↺ Сбросить
          </button>
        )}
      </div>
      {Array.from(sectionMap.entries()).map(([sec, flds]) => (
        <FPAccordion key={sec} title={sec}>
          {flds.map(f => renderField(f))}
        </FPAccordion>
      ))}
    </div>
  )
}

function BlockProperties({
  selection,
  updateBlock,
  setBlockAbTest,
  setBlockCustomCode,
}: {
  selection: { block: BlockState; sectionId: string }
  updateBlock: (sectionId: string, blockId: string, data: Record<string, unknown>) => void
  setBlockAbTest: (sectionId: string, blockId: string, abTestId: string, abVariant: 'A' | 'B' | '') => void
  setBlockCustomCode: (sectionId: string, blockId: string, css: string, js: string) => void
}) {
  const def = getBlock(selection.block.type)
  const { block, sectionId } = selection
  const page = useEditorStore(s => s.page)
  const setBlockWrapper = useEditorStore(s => s.setBlockWrapper)
  const [cmsFields, setCmsFields] = useState<string[]>([])

  useEffect(() => {
    const slug = page.cmsTemplateFor
    if (!slug) { setCmsFields([]); return }
    const siteId = localStorage.getItem('bst-site-id')
    if (!siteId) return
    cmsApi.listCollections(siteId).then(cols => {
      const col = cols.find((c: { slug: string; schema: Array<{ name: string }> }) => c.slug === slug)
      setCmsFields(col ? col.schema.map((f: { name: string }) => f.name) : [])
    }).catch(() => setCmsFields([]))
  }, [page.cmsTemplateFor])

  function handleChange(key: string, value: unknown) {
    updateBlock(sectionId, block.id, { [key]: value })
  }

  const [saving, setSaving] = useState(false)
  const [savedOk, setSavedOk] = useState(false)

  async function handleSaveAsComponent() {
    setSaving(true)
    try {
      const name = prompt('Название компонента:', def?.label ?? block.type)
      if (!name?.trim()) return
      await savedBlocksApi.create({ name: name.trim(), icon: def?.icon ?? '📌', blockType: block.type, data: block.data as Record<string, unknown> })
      setSavedOk(true)
      setTimeout(() => setSavedOk(false), 2000)
    } catch { /* ignore */ } finally {
      setSaving(false)
    }
  }

  return (
    <div className="properties-panel">
      <div style={{ padding: '6px 10px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="text"
          className="prop-input"
          value={block.blockLinkUrl ?? ''}
          placeholder="🔗 URL-адрес блока"
          onChange={e => setBlockWrapper(sectionId, block.id, { blockLinkUrl: e.target.value || undefined })}
          style={{ fontSize: '11px', flex: 1 }}
        />
        {block.blockLinkUrl && (
          <select
            className="prop-input"
            value={block.blockLinkTarget ?? '_self'}
            onChange={e => setBlockWrapper(sectionId, block.id, { blockLinkTarget: e.target.value as '_blank' | '_self' })}
            style={{ fontSize: '11px', width: 'auto', flexShrink: 0 }}
          >
            <option value="_self">Эта вкладка</option>
            <option value="_blank">Новая вкладка</option>
          </select>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)' }}>
        <div style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{def?.label ?? block.type}</div>
        <button
          onClick={handleSaveAsComponent}
          disabled={saving}
          title="Сохранить как компонент"
          style={{ padding: '4px 8px', background: savedOk ? '#22c55e' : 'var(--bg-3)', border: `1px solid ${savedOk ? '#22c55e' : 'var(--border)'}`, borderRadius: '6px', fontSize: '11px', color: savedOk ? '#fff' : 'var(--text-muted)', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {savedOk ? '✓ Сохранено' : '📌'}
        </button>
      </div>
      {cmsFields.length > 0 && (
        <div style={{ margin: '0 0 8px', padding: '6px 10px', background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: '6px', fontSize: '11px', color: 'var(--accent)' }}>
          📋 CMS-шаблон: <b>{page.cmsTemplateFor}</b> — нажмите <b>{'{}'}</b> рядом с текстовым полем, чтобы вставить переменную
        </div>
      )}
      {block.type === 'swiss-page'
        ? <SwissPageProperties block={block} onChange={handleChange} />
        : def && Object.keys(def.schema).length > 0
            ? <>
                {Object.entries(def.schema).map(([key, field]) => (
                  <FieldControl
                    key={key}
                    fieldKey={key}
                    field={field}
                    value={block.data[key]}
                    onChange={(v) => handleChange(key, v)}
                    blockType={block.type}
                    cmsFields={cmsFields}
                  />
                ))}
                {(LANDING_STYLE_META[block.type] ?? PORTFOLIO_META[block.type]) && (
                  <div style={{ padding: '12px 12px 8px', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6, borderTop: '1px solid var(--border)', marginTop: '4px' }}>
                    <b style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '4px' }}>
                      {(LANDING_STYLE_META[block.type] ?? PORTFOLIO_META[block.type])?.[0]}
                    </b>
                    {(LANDING_STYLE_META[block.type] ?? PORTFOLIO_META[block.type])?.[1]}
                  </div>
                )}
              </>
            : PORTFOLIO_META[block.type]
              ? <div>
                  <div style={{ padding: '12px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '8px' }}>
                      Стиль страницы
                    </div>
                    <div style={{ padding: '12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>
                        {PORTFOLIO_META[block.type][0]}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        {PORTFOLIO_META[block.type][1]}
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Редактирование текста — в разделе «✏ Текст страницы» ниже.
                  </div>
                  <div style={{ height: '40px' }} />
                </div>
              : <div className="prop-no-schema">Нет схемы для этого блока</div>
      }
      {FULL_PAGE_TYPES.includes(block.type) && block.type !== 'swiss-page' && (
        <FullPageContentEditor block={block} sectionId={sectionId} updateBlock={updateBlock} />
      )}
      {block.type === 'ai-image' && (
        <AiImageSection block={block} sectionId={sectionId} updateBlock={updateBlock} />
      )}
      {!FULL_PAGE_TYPES.includes(block.type) && <BlockStylePicker block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <TypographySection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <ColorsSection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <ButtonStyleSection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <VisualEffectsSection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <ResponsiveOverridesSection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <AnimationSection block={block} sectionId={sectionId} updateBlock={updateBlock} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <VisibilitySection block={block} sectionId={sectionId} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <AbTestSection block={block} sectionId={sectionId} setBlockAbTest={setBlockAbTest} />}
      {!FULL_PAGE_TYPES.includes(block.type) && <CustomCodeSection block={block} sectionId={sectionId} setBlockCustomCode={setBlockCustomCode} />}
    </div>
  )
}

function AiImageSection({ block, sectionId, updateBlock }: {
  block: BlockState
  sectionId: string
  updateBlock: (sectionId: string, blockId: string, data: Record<string, unknown>) => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleGenerate() {
    const prompt = String(block.data.prompt ?? '').trim()
    if (!prompt) { setError('Введите prompt для генерации'); return }
    setLoading(true)
    setError('')
    const seed = Math.floor(Math.random() * 999999)
    const w = Math.min(2048, Math.max(256, Number(block.data.genWidth ?? 1200)))
    const h = Math.min(2048, Math.max(256, Number(block.data.genHeight ?? 800)))
    const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=${w}&height=${h}&seed=${seed}&model=flux&nologo=true`
    try {
      const res = await fetch(url, { method: 'HEAD' })
      if (!res.ok) throw new Error('Ошибка генерации')
      updateBlock(sectionId, block.id, { imageUrl: url, seed: String(seed) })
    } catch {
      setError('Не удалось сгенерировать. Проверьте интернет-соединение.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="prop-group" style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>🤖 AI Генерация</div>
      <button
        onClick={handleGenerate}
        disabled={loading}
        style={{
          width: '100%', padding: '9px 14px',
          background: loading ? 'var(--bg-3)' : 'linear-gradient(135deg, #5b6af0, #8b5cf6)',
          color: loading ? 'var(--text-muted)' : '#fff',
          border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        }}
      >
        {loading
          ? <><span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Генерирую...</>
          : '✨ Сгенерировать изображение'
        }
      </button>
      {error && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px' }}>{error}</div>}
      {!!block.data.imageUrl && (
        <div style={{ marginTop: '8px' }}>
          <img src={String(block.data.imageUrl)} alt="preview" style={{ width: '100%', borderRadius: '8px', border: '1px solid var(--border)' }} loading="lazy" />
          <button
            onClick={() => updateBlock(sectionId, block.id, { imageUrl: '', seed: '' })}
            style={{ marginTop: '5px', background: 'none', border: 'none', fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}
          >
            Удалить изображение
          </button>
        </div>
      )}
    </div>
  )
}

type BgType = 'transparent' | 'color' | 'gradient' | 'image' | 'pattern' | 'video'

const BG_PATTERNS = [
  { id: 'dots', label: 'Точки', bg: 'radial-gradient(rgba(0,0,0,0.12) 1.5px, #fff 1.5px)', size: '22px 22px' },
  { id: 'dots-dark', label: 'Точки (тёмные)', bg: 'radial-gradient(rgba(0,0,0,0.22) 1.5px, #f8f9fa 1.5px)', size: '22px 22px' },
  { id: 'grid', label: 'Сетка', bg: 'linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.08) 1px, transparent 1px)', size: '24px 24px' },
  { id: 'diagonal', label: 'Диагонали', bg: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 0, transparent 12px)', size: '12px 12px' },
  { id: 'cross', label: 'Крест', bg: 'repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(0,0,0,0.07) 23px, rgba(0,0,0,0.07) 24px), repeating-linear-gradient(90deg, transparent, transparent 23px, rgba(0,0,0,0.07) 23px, rgba(0,0,0,0.07) 24px)', size: '24px 24px' },
  { id: 'accent-dots', label: 'Акцент-точки', bg: 'radial-gradient(rgba(91,106,240,0.18) 2px, #f0f4ff 2px)', size: '24px 24px' },
]

function parseBgType(bg: string): BgType {
  if (!bg || bg === 'transparent') return 'transparent'
  if (bg.startsWith('linear-gradient')) return 'gradient'
  if (bg.startsWith('url(')) return 'image'
  if (bg.startsWith('radial-gradient') || bg.startsWith('repeating-')) return 'pattern'
  return 'color'
}

function parseGradientParts(bg: string) {
  const m = bg.match(/linear-gradient\((\d+)deg,\s*([^,]+),\s*([^)]+)\)/)
  return m
    ? { angle: parseInt(m[1]), from: m[2].trim(), to: m[3].trim() }
    : { angle: 135, from: '#5b6af0', to: '#a78bfa' }
}

function parseImageUrl(bg: string) {
  const m = bg.match(/url\(['"]?([^'")\s]+)['"]?\)/)
  return m ? m[1] : ''
}

function SectionProperties({
  paddingTop, paddingBottom, background, backgroundSize, backgroundPosition,
  backgroundVideo, bgOverlay, bgOverlayOpacity, fullHeight, minHeight, sectionAnimation, anchorId, sectionId, sectionName, sectionStyle, sectionLayout, onChange,
}: {
  paddingTop: number
  paddingBottom: number
  background: string
  backgroundSize?: string
  backgroundPosition?: string
  backgroundVideo?: string
  bgOverlay?: string
  bgOverlayOpacity?: number
  fullHeight?: boolean
  minHeight?: number
  sectionAnimation?: string
  anchorId?: string
  sectionId?: string
  sectionName?: string
  sectionStyle?: string
  sectionLayout?: SectionLayout
  onChange: (patch: Record<string, unknown>) => void
}) {
  const initType: BgType = backgroundVideo ? 'video' : parseBgType(background)
  const initGrad = parseGradientParts(background)
  const [bgType, setBgType] = useState<BgType>(initType)
  const [gradFrom, setGradFrom] = useState(initGrad.from)
  const [gradTo, setGradTo] = useState(initGrad.to)
  const [gradAngle, setGradAngle] = useState(initGrad.angle)
  const [imgUrl, setImgUrl] = useState(parseImageUrl(background))
  const [bgSize, setBgSize] = useState(backgroundSize ?? 'cover')
  const [bgPos, setBgPos] = useState(backgroundPosition ?? 'center')
  const [showMediaLib, setShowMediaLib] = useState(false)

  function applyType(t: BgType) {
    setBgType(t)
    if (t === 'transparent') onChange({ background: 'transparent', backgroundSize: undefined, backgroundPosition: undefined, backgroundVideo: undefined })
    else if (t === 'color') onChange({ background: '#ffffff', backgroundSize: undefined, backgroundPosition: undefined, backgroundVideo: undefined })
    else if (t === 'gradient') onChange({ background: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})`, backgroundVideo: undefined })
    else if (t === 'image' && imgUrl) onChange({ background: `url(${imgUrl})`, backgroundSize: bgSize, backgroundPosition: bgPos, backgroundVideo: undefined })
    else if (t === 'image') onChange({ background: 'transparent', backgroundVideo: undefined })
    else if (t === 'pattern') { const p = BG_PATTERNS[0]; onChange({ background: p.bg, backgroundSize: p.size, backgroundPosition: 'center', backgroundVideo: undefined }) }
    else if (t === 'video') onChange({ background: 'transparent', backgroundVideo: backgroundVideo || '' })
  }

  function applyGradient(from?: string, to?: string, angle?: number) {
    const f = from ?? gradFrom; const t = to ?? gradTo; const a = angle ?? gradAngle
    onChange({ background: `linear-gradient(${a}deg, ${f}, ${t})` })
  }

  function applyImage(url?: string, size?: string, pos?: string) {
    const u = url ?? imgUrl; const s = size ?? bgSize; const p = pos ?? bgPos
    if (u) onChange({ background: `url(${u})`, backgroundSize: s, backgroundPosition: p })
  }

  const typeLabels: Record<BgType, string> = {
    transparent: 'Нет', color: 'Цвет', gradient: 'Градиент', image: 'Картинка', pattern: 'Узор', video: 'Видео',
  }

  return (
    <div className="properties-panel">
      <div className="properties-header">Секция</div>

      <div className="prop-group">
        <label className="prop-label">Название секции</label>
        <input className="prop-input" type="text" value={sectionName ?? ''}
          placeholder="Необязательно (для слоёв)"
          onChange={(e) => onChange({ name: e.target.value || undefined })}
        />
      </div>

      <div className="prop-group">
        <label className="prop-label">Отступ сверху (px)</label>
        <input type="number" className="prop-input" value={paddingTop} min={0} max={400}
          onChange={(e) => onChange({ paddingTop: Number(e.target.value) })} />
      </div>
      <div className="prop-group">
        <label className="prop-label">Отступ снизу (px)</label>
        <input type="number" className="prop-input" value={paddingBottom} min={0} max={400}
          onChange={(e) => onChange({ paddingBottom: Number(e.target.value) })} />
      </div>

      <div className="prop-group">
        <label className="prop-label">Тип фона</label>
        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
          {(['transparent', 'color', 'gradient', 'image', 'pattern', 'video'] as BgType[]).map((t) => (
            <button key={t} onClick={() => applyType(t)}
              style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '5px', fontWeight: bgType === t ? 600 : 400, background: bgType === t ? 'var(--accent)' : 'var(--bg-3)', color: bgType === t ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}>
              {typeLabels[t]}
            </button>
          ))}
        </div>
      </div>

      {bgType === 'color' && (
        <div className="prop-group">
          <label className="prop-label">Цвет фона</label>
          <input type="color" className="prop-color"
            value={background && !['transparent','linear-gradient','url'].some(x => background.startsWith(x)) ? background : '#ffffff'}
            onChange={(e) => onChange({ background: e.target.value })} />
        </div>
      )}

      {bgType === 'gradient' && (
        <>
          <div className="prop-group">
            <label className="prop-label">Цвет 1</label>
            <input type="color" className="prop-color" value={gradFrom}
              onChange={(e) => { setGradFrom(e.target.value); applyGradient(e.target.value) }} />
          </div>
          <div className="prop-group">
            <label className="prop-label">Цвет 2</label>
            <input type="color" className="prop-color" value={gradTo}
              onChange={(e) => { setGradTo(e.target.value); applyGradient(undefined, e.target.value) }} />
          </div>
          <div className="prop-group">
            <label className="prop-label">Угол: {gradAngle}°</label>
            <input type="range" min={0} max={360} value={gradAngle} style={{ width: '100%', accentColor: 'var(--accent)' }}
              onChange={(e) => { setGradAngle(Number(e.target.value)); applyGradient(undefined, undefined, Number(e.target.value)) }} />
          </div>
          <div className="prop-group">
            <div style={{ height: '40px', borderRadius: '6px', background: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` }} />
          </div>
        </>
      )}

      {showMediaLib && (
        <Suspense fallback={null}>
          <MediaLibraryModal
            onSelect={(url) => { setImgUrl(url); applyImage(url); setShowMediaLib(false) }}
            onClose={() => setShowMediaLib(false)}
          />
        </Suspense>
      )}

      {bgType === 'image' && (
        <>
          <div className="prop-group">
            <label className="prop-label">URL изображения</label>
            <textarea className="prop-textarea" rows={2} placeholder="https://..."
              value={imgUrl}
              onChange={(e) => { setImgUrl(e.target.value); applyImage(e.target.value) }} />
            <button
              onClick={() => setShowMediaLib(true)}
              style={{ marginTop: '5px', width: '100%', padding: '6px', fontSize: '11px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 500 }}
            >
              📁 Выбрать из медиатеки
            </button>
            {imgUrl && (
              <img src={imgUrl} alt="" style={{ marginTop: '6px', width: '100%', height: '60px', objectFit: 'cover', borderRadius: '5px', display: 'block' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
            )}
          </div>
          <div className="prop-group">
            <label className="prop-label">Размер</label>
            <select className="prop-select" value={bgSize}
              onChange={(e) => { setBgSize(e.target.value); applyImage(undefined, e.target.value) }}>
              <option value="cover">Cover — заполнить</option>
              <option value="contain">Contain — вписать</option>
              <option value="auto">Auto</option>
            </select>
          </div>
          <div className="prop-group">
            <label className="prop-label">Позиция</label>
            <select className="prop-select" value={bgPos}
              onChange={(e) => { setBgPos(e.target.value); applyImage(undefined, undefined, e.target.value) }}>
              <option value="center">Центр</option>
              <option value="top">Сверху</option>
              <option value="bottom">Снизу</option>
              <option value="left">Слева</option>
              <option value="right">Справа</option>
            </select>
          </div>
        </>
      )}

      {bgType === 'pattern' && (
        <div className="prop-group">
          <label className="prop-label">Выберите узор</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {BG_PATTERNS.map(p => (
              <button
                key={p.id}
                onClick={() => onChange({ background: p.bg, backgroundSize: p.size, backgroundPosition: 'center' })}
                style={{
                  height: '48px', borderRadius: '6px', border: `2px solid ${background === p.bg ? 'var(--accent)' : 'var(--border)'}`,
                  background: p.bg, backgroundSize: p.size, cursor: 'pointer', display: 'flex', alignItems: 'flex-end', padding: '3px 5px',
                }}
                title={p.label}
              >
                <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.85)', padding: '1px 4px', borderRadius: '3px', color: '#333' }}>{p.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {bgType === 'video' && (
        <div className="prop-group">
          <label className="prop-label">URL видео (MP4)</label>
          <textarea className="prop-textarea" rows={2} placeholder="https://example.com/video.mp4"
            value={backgroundVideo || ''}
            onChange={(e) => onChange({ backgroundVideo: e.target.value || undefined })} />
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Используйте MP4/WebM. Видео будет автовоспроизводиться без звука.
          </div>
        </div>
      )}

      {(bgType === 'image' || bgType === 'color' || bgType === 'gradient' || bgType === 'video') && (
        <>
          <div className="prop-group">
            <label className="prop-label">Цветовой оверлей</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="color" className="prop-color" value={bgOverlay || '#000000'}
                onChange={(e) => onChange({ bgOverlay: e.target.value })} />
              <button
                onClick={() => onChange({ bgOverlay: bgOverlay ? undefined : '#000000', bgOverlayOpacity: 50 })}
                style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '5px', background: bgOverlay ? 'var(--accent)' : 'var(--bg-3)', color: bgOverlay ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}>
                {bgOverlay ? 'Убрать' : 'Добавить'}
              </button>
            </div>
          </div>
          {bgOverlay && (
            <div className="prop-group">
              <label className="prop-label">Прозрачность оверлея: {bgOverlayOpacity ?? 50}%</label>
              <input type="range" min={0} max={100} value={bgOverlayOpacity ?? 50}
                style={{ width: '100%', accentColor: 'var(--accent)' }}
                onChange={(e) => onChange({ bgOverlayOpacity: Number(e.target.value) })} />
            </div>
          )}
        </>
      )}

      <div className="prop-group" style={{ paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
        <label className="prop-label">Высота секции</label>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {[
            { key: 'auto', label: 'Авто' },
            { key: 'full', label: '100vh' },
            { key: 'custom', label: 'Задать' },
          ].map(opt => (
            <button key={opt.key}
              onClick={() => {
                if (opt.key === 'auto') onChange({ fullHeight: false, minHeight: undefined })
                else if (opt.key === 'full') onChange({ fullHeight: true, minHeight: undefined })
                else onChange({ fullHeight: false, minHeight: 400 })
              }}
              style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                background: (opt.key === 'full' && fullHeight) || (opt.key === 'custom' && !fullHeight && minHeight) || (opt.key === 'auto' && !fullHeight && !minHeight) ? 'var(--accent)' : 'var(--bg-3)',
                color: (opt.key === 'full' && fullHeight) || (opt.key === 'custom' && !fullHeight && minHeight) || (opt.key === 'auto' && !fullHeight && !minHeight) ? '#fff' : 'var(--text-secondary)',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        {!fullHeight && minHeight !== undefined && (
          <input type="number" className="prop-input" style={{ marginTop: '6px' }} value={minHeight} min={100} max={2000}
            onChange={(e) => onChange({ minHeight: Number(e.target.value) })} />
        )}
      </div>

      <div className="prop-group">
        <label className="prop-label">Анимация секции</label>
        <select className="prop-select" value={sectionAnimation || 'none'}
          onChange={(e) => onChange({ sectionAnimation: e.target.value === 'none' ? undefined : e.target.value })}>
          <option value="none">Нет</option>
          <option value="fade-up">Снизу вверх</option>
          <option value="fade-down">Сверху вниз</option>
          <option value="fade-left">Слева</option>
          <option value="fade-right">Справа</option>
          <option value="fade-in">Появление</option>
          <option value="zoom-in">Масштаб</option>
        </select>
      </div>

      <div className="prop-group">
        <label className="prop-label">Якорная ссылка</label>
        <input
          className="prop-input"
          type="text"
          value={anchorId || ''}
          placeholder={`sec-${sectionId?.slice(0, 8) ?? ''}`}
          onChange={(e) => onChange({ anchorId: e.target.value.replace(/[^a-z0-9-_]/gi, '') || undefined })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.5 }}>
          Ссылка вида <code style={{ fontFamily: 'monospace' }}>#{anchorId || `sec-${sectionId?.slice(0, 8) ?? '...'}`}</code>. Используйте в навигации.
        </div>
      </div>

      <div className="prop-group" style={{ paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
        <label className="prop-label">Стиль секции</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginTop: '6px' }}>
          <button
            onClick={() => onChange({ sectionStyle: undefined })}
            style={{
              padding: '6px 4px', fontSize: '11px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center',
              border: !sectionStyle ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: !sectionStyle ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)',
              fontWeight: !sectionStyle ? 700 : 400, color: 'var(--text-secondary)',
            }}
          >
            <span style={{ fontSize: '14px' }}>○</span> Без стиля
          </button>
          {DESIGN_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => onChange({ sectionStyle: style.id })}
              style={{
                padding: '6px 4px', fontSize: '11px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center',
                border: sectionStyle === style.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                background: sectionStyle === style.id ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)',
                fontWeight: sectionStyle === style.id ? 700 : 400, color: 'var(--text-secondary)',
              }}
            >
              <span style={{ color: style.previewAccent }}>{style.emoji}</span> {style.label}
            </button>
          ))}
        </div>
        {sectionStyle && (() => {
          const active = DESIGN_STYLES.find(s => s.id === sectionStyle)
          return active ? (
            <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {active.emoji} <strong>{active.label}</strong> — {active.description}
            </div>
          ) : null
        })()}
      </div>
      {sectionId && <SectionLayoutSection sectionId={sectionId} layout={sectionLayout} />}
    </div>
  )
}

function ArrayFieldEditor({
  field,
  value,
  onChange,
}: {
  field: FieldSchema
  value: unknown
  onChange: (v: unknown) => void
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const items = Array.isArray(value) ? value : []
  const { itemSchema, stringArray } = field

  function addItem() {
    const newItem = stringArray
      ? ''
      : itemSchema
      ? Object.fromEntries(Object.entries(itemSchema).map(([k, s]) => [k, s.default ?? '']))
      : {}
    const next = [...items, newItem]
    onChange(next)
    setExpandedIdx(next.length - 1)
  }

  function removeItem(idx: number) {
    onChange(items.filter((_, i) => i !== idx))
    if (expandedIdx === idx) setExpandedIdx(null)
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1)
  }

  function updateItem(idx: number, newVal: unknown) {
    onChange(items.map((it, i) => (i === idx ? newVal : it)))
  }

  function getItemLabel(item: unknown, idx: number): string {
    if (stringArray || typeof item === 'string') return String(item) || `Элемент ${idx + 1}`
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>
      const firstKey = Object.keys(itemSchema ?? {})[0]
      return firstKey ? String(obj[firstKey] ?? '') || `Элемент ${idx + 1}` : `Элемент ${idx + 1}`
    }
    return `Элемент ${idx + 1}`
  }

  return (
    <div className="prop-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label className="prop-label" style={{ marginBottom: 0 }}>{field.label}</label>
        <button className="btn-ghost btn-sm" onClick={addItem} style={{ fontSize: '11px' }}>+ Добавить</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {items.map((item, idx) => (
          <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', padding: '5px 8px', background: 'var(--bg-2)', cursor: 'pointer', gap: '6px' }}
              onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            >
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', minWidth: '14px' }}>{idx + 1}</span>
              <span style={{ flex: 1, fontSize: '11px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getItemLabel(item, idx)}
              </span>
              <button
                style={{ fontSize: '10px', color: '#ef4444', padding: '1px 4px', borderRadius: '3px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); removeItem(idx) }}
              >✕</button>
              <span style={{ fontSize: '9px', color: 'var(--text-muted)', flexShrink: 0 }}>{expandedIdx === idx ? '▲' : '▼'}</span>
            </div>
            {expandedIdx === idx && (
              <div style={{ padding: '8px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '6px', background: 'var(--bg-1)' }}>
                {stringArray ? (
                  <textarea
                    className="prop-textarea"
                    rows={2}
                    value={String(item)}
                    onChange={(e) => updateItem(idx, e.target.value)}
                  />
                ) : itemSchema ? (
                  Object.entries(itemSchema).map(([key, schema]) => (
                    <div key={key}>
                      <label className="prop-label">{schema.label}</label>
                      {schema.type === 'boolean' ? (
                        <label className="prop-toggle">
                          <input
                            type="checkbox"
                            checked={Boolean((item as Record<string, unknown>)[key])}
                            onChange={(e) => updateItem(idx, { ...(item as Record<string, unknown>), [key]: e.target.checked })}
                          />
                          <span className="toggle-track" />
                        </label>
                      ) : schema.type === 'select' ? (
                        <select
                          className="prop-select"
                          value={String((item as Record<string, unknown>)[key] ?? schema.default ?? '')}
                          onChange={(e) => updateItem(idx, { ...(item as Record<string, unknown>), [key]: e.target.value })}
                        >
                          {schema.options?.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          className="prop-textarea"
                          rows={schema.type === 'richtext' ? 3 : 1}
                          placeholder={schema.placeholder}
                          value={String((item as Record<string, unknown>)[key] ?? '')}
                          onChange={(e) => updateItem(idx, { ...(item as Record<string, unknown>), [key]: e.target.value })}
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Нет схемы элемента</div>
                )}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '8px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '6px' }}>
            Нет элементов — нажмите «+ Добавить»
          </div>
        )}
      </div>
    </div>
  )
}

function AiTextButton({ fieldLabel, blockType, onResult }: { fieldLabel: string; blockType: string; onResult: (text: string) => void }) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)

  async function generate() {
    if (!prompt.trim()) return
    setLoading(true)
    try {
      const res = await api.post<{ text: string }>('/ai/generate-copy', { blockType, context: `Field: ${fieldLabel}. Request: ${prompt}` })
      onResult(res.text)
      setOpen(false)
      setPrompt('')
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="AI: сгенерировать текст"
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '0 2px', color: 'var(--accent)', opacity: 0.75, lineHeight: 1 }}
      >✨</button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '22px', zIndex: 20, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px', width: '220px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>AI генерация текста</div>
          <textarea
            autoFocus
            rows={3}
            placeholder="Опишите что нужно написать..."
            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-2)', color: 'var(--text-primary)', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) generate() }}
          />
          <button
            onClick={generate}
            disabled={loading || !prompt.trim()}
            style={{ marginTop: '6px', width: '100%', padding: '6px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >{loading ? '...' : '✨ Создать'}</button>
        </div>
      )}
    </div>
  )
}

function CmsPicker({ fields, onInsert }: { fields: string[]; onInsert: (field: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!fields.length) return null
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        title="Вставить поле из CMS"
        onClick={() => setOpen(o => !o)}
        style={{
          background: open ? 'var(--accent)' : 'var(--bg-3)',
          color: open ? '#fff' : 'var(--accent)',
          border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: '5px', padding: '2px 7px', fontSize: '11px',
          fontFamily: 'monospace', cursor: 'pointer', fontWeight: 700,
        }}
      >
        {'{}'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 200,
          background: 'var(--bg-1)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '6px', display: 'flex',
          flexDirection: 'column', gap: '3px', minWidth: '150px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', paddingBottom: '3px', fontWeight: 600 }}>Поля коллекции:</div>
          {fields.map(f => (
            <button
              key={f}
              onClick={() => { onInsert(f); setOpen(false) }}
              style={{
                textAlign: 'left', background: 'var(--bg-2)', border: '1px solid var(--border)',
                borderRadius: '5px', padding: '4px 8px', fontSize: '11px',
                fontFamily: 'monospace', cursor: 'pointer', color: 'var(--accent)',
              }}
            >
              {`{{${f}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function FieldControl({
  fieldKey,
  field,
  value,
  onChange,
  blockType,
  cmsFields,
}: {
  fieldKey: string
  field: FieldSchema
  value: unknown
  onChange: (v: unknown) => void
  blockType?: string
  cmsFields?: string[]
}) {
  const rawDefault = field.default
  const strDefault = rawDefault === undefined || rawDefault === null || typeof rawDefault === 'object' ? '' : String(rawDefault)
  const strVal = value === undefined || value === null ? strDefault : (typeof value === 'object' ? '' : String(value))
  const taRef = useRef<HTMLTextAreaElement>(null)

  function insertCmsField(fieldName: string) {
    const ta = taRef.current
    const token = `{{${fieldName}}}`
    if (!ta) { onChange(strVal + token); return }
    const start = ta.selectionStart ?? strVal.length
    const end = ta.selectionEnd ?? strVal.length
    onChange(strVal.slice(0, start) + token + strVal.slice(end))
    requestAnimationFrame(() => {
      ta.selectionStart = ta.selectionEnd = start + token.length
      ta.focus()
    })
  }

  switch (field.type) {
    case 'text':
    case 'url':
    case 'richtext':
      return (
        <div className="prop-group">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
            <label className="prop-label" style={{ flex: 1 }}>{field.label}</label>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {field.type !== 'url' && blockType && (
                <AiTextButton fieldLabel={field.label} blockType={blockType} onResult={v => onChange(v)} />
              )}
              {cmsFields && cmsFields.length > 0 && (
                <CmsPicker fields={cmsFields} onInsert={insertCmsField} />
              )}
            </div>
          </div>
          <textarea
            ref={taRef}
            className="prop-textarea"
            rows={field.type === 'richtext' ? 4 : 2}
            placeholder={field.placeholder}
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )

    case 'image':
      return <ImageField value={strVal} label={field.label} onChange={onChange} />

    case 'number':
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <input
            type="number"
            className="prop-input"
            value={strVal}
            min={field.min}
            max={field.max}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        </div>
      )

    case 'boolean':
      return (
        <div className="prop-group prop-group--inline">
          <label className="prop-label">{field.label}</label>
          <label className="prop-toggle">
            <input
              type="checkbox"
              checked={value === undefined ? Boolean(field.default) : Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="toggle-track" />
          </label>
        </div>
      )

    case 'select':
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <select
            className="prop-select"
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )

    case 'color':
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <input
            type="color"
            className="prop-color"
            value={strVal || '#000000'}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )

    case 'font': {
      const FONTS = [
        { label: 'По умолчанию', value: '' },
        { label: 'Inter', value: 'Inter' },
        { label: 'Roboto', value: 'Roboto' },
        { label: 'Open Sans', value: 'Open Sans' },
        { label: 'Montserrat', value: 'Montserrat' },
        { label: 'Playfair Display', value: 'Playfair Display' },
        { label: 'Raleway', value: 'Raleway' },
        { label: 'Nunito', value: 'Nunito' },
        { label: 'Poppins', value: 'Poppins' },
        { label: 'Lora', value: 'Lora' },
        { label: 'Merriweather', value: 'Merriweather' },
        { label: 'PT Sans', value: 'PT Sans' },
        { label: 'PT Serif', value: 'PT Serif' },
        { label: 'Arial', value: 'Arial, sans-serif' },
        { label: 'Georgia', value: 'Georgia, serif' },
        { label: 'monospace', value: 'monospace' },
      ]
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <select className="prop-select" value={strVal} onChange={(e) => onChange(e.target.value)}>
            {FONTS.map((f) => <option key={f.value} value={f.value} style={{ fontFamily: f.value || undefined }}>{f.label}</option>)}
          </select>
          {strVal && <div style={{ marginTop: '4px', fontSize: '14px', fontFamily: strVal, color: 'var(--text-primary)', padding: '4px 0' }}>Пример текста · Sample text</div>}
        </div>
      )
    }

    case 'icon': {
      const ICONS = ['😀','🚀','⚡','🎨','💡','🔒','📊','✓','✉','📍','✆','🏆','❤','⭐','🌟','💎','🔥','🎯','✅','📱','💻','🌍','🎁','📢','🔑','⚙','🛡','📈','👥','💬','🔔','💰']
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px', marginBottom: '4px' }}>
            {ICONS.map((icon) => (
              <button
                key={icon}
                onClick={() => onChange(icon)}
                style={{
                  fontSize: '18px', padding: '4px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                  background: strVal === icon ? 'var(--accent)' : 'var(--bg-3)',
                  outline: strVal === icon ? '2px solid var(--accent)' : 'none',
                }}
              >
                {icon}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="prop-input"
            placeholder="Или введите своё…"
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            style={{ marginTop: '4px' }}
          />
        </div>
      )
    }

    case 'array':
      return <ArrayFieldEditor field={field} value={value} onChange={onChange} />

    default:
      return (
        <div className="prop-group">
          <label className="prop-label">{field.label}</label>
          <div className="prop-unsupported">Тип: {fieldKey}</div>
        </div>
      )
  }
}

function ImageField({ value, label, onChange }: { value: string; label?: string; onChange: (v: string) => void }) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [libraryOpen, setLibraryOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    setUploading(true)
    setError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const token = localStorage.getItem('bst-token') ?? ''
      const res = await fetch(`${BASE}/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json() as { url: string }
      onChange(data.url)
    } catch {
      setError('Ошибка загрузки')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="prop-group">
      {label && <label className="prop-label">{label}</label>}
      <input
        type="text"
        className="prop-input"
        placeholder="https://... или /path/to/image.jpg"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
        <label
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', cursor: uploading ? 'wait' : 'pointer', fontSize: '11px', color: 'var(--text-secondary)', padding: '5px 8px', borderRadius: '5px', border: '1px dashed var(--border)', justifyContent: 'center', opacity: uploading ? 0.6 : 1 }}
        >
          <span>{uploading ? '...' : '📁 Загрузить'}</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) handleFile(file)
            }}
          />
        </label>
        <button
          onClick={() => setLibraryOpen(true)}
          style={{ padding: '5px 8px', fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer' }}
          title="Медиатека"
        >🗂</button>
      </div>
      {error && <div style={{ fontSize: '10px', color: '#f87171', marginTop: '3px' }}>{error}</div>}
      {libraryOpen && (
        <Suspense fallback={null}>
          <MediaLibraryModal onSelect={url => { onChange(url); setLibraryOpen(false) }} onClose={() => setLibraryOpen(false)} />
        </Suspense>
      )}
      {value && (
        <div style={{ marginTop: '6px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border)', maxHeight: '100px' }}>
          <img src={value} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
        </div>
      )}
    </div>
  )
}

function AiSeoButton({ title, pageContent, onResult }: { title: string; pageContent: string; onResult: (seoTitle: string, seoDesc: string) => void }) {
  const [loading, setLoading] = useState(false)

  async function run() {
    setLoading(true)
    try {
      const res = await api.post<{ seoTitle: string; seoDesc: string; keywords: string[] }>('/ai/improve-seo', { title, content: pageContent })
      onResult(res.seoTitle, res.seoDesc)
    } catch {
      // silent fail
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      onClick={run}
      disabled={loading}
      title="Сгенерировать SEO title и description с помощью AI"
      style={{ padding: '3px 8px', background: loading ? 'var(--bg-3)' : 'linear-gradient(135deg,#5b6af0,#9b59f5)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
    >
      {loading ? '⏳' : '✨'} AI SEO
    </button>
  )
}

const LANG_LIST = [
  { code: 'en', name: 'English', flag: '🇺🇸' }, { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }, { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' }, { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' }, { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }, { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' }, { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' }, { code: 'kk', name: 'Қазақша', flag: '🇰🇿' },
  { code: 'uz', name: "O'zbek", flag: '🇺🇿' },
]

function PageTranslationsSection({ pageSlug, pageSections }: { pageSlug: string; pageSections: unknown[] }) {
  const { settings } = useEditorStore()
  const [translations, setTranslations] = useState<Record<string, PageTranslation>>({})
  const [translating, setTranslating] = useState<string | null>(null)
  const siteId = localStorage.getItem('bst-site-id')
  const enabledLangs = settings.additionalLanguages ?? []

  const load = useCallback(async () => {
    if (!siteId || !pageSlug) return
    try {
      const res = await translationsApi.get(siteId, pageSlug)
      setTranslations(res.translations ?? {})
    } catch { setTranslations({}) }
  }, [siteId, pageSlug])

  useEffect(() => { load() }, [load])

  if (enabledLangs.length === 0) return null

  return (
    <div className="prop-group">
      <label className="prop-label" style={{ color: 'var(--accent)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
        🌐 Переводы
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
        {enabledLangs.map(lang => {
          const info = LANG_LIST.find(l => l.code === lang)
          const hasTr = !!translations[lang]
          const isBusy = translating === lang
          return (
            <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', background: 'var(--bg-2)', borderRadius: '6px', border: `1px solid ${hasTr ? 'var(--accent)' : 'var(--border)'}` }}>
              <span style={{ fontSize: '14px' }}>{info?.flag ?? '🌐'}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, flex: 1 }}>{info?.name ?? lang.toUpperCase()}</span>
              {hasTr && <span style={{ fontSize: '10px', color: 'var(--accent)' }}>✓</span>}
              <button
                onClick={async () => {
                  if (!siteId) return
                  setTranslating(lang)
                  try {
                    const res = await aiTranslateApi.translatePage({ sections: pageSections }, info?.name ?? lang)
                    await translationsApi.save(siteId, pageSlug, lang, { sections: res.sections })
                    setTranslations(prev => ({ ...prev, [lang]: { sections: res.sections } }))
                  } catch (e) {
                    alert('Ошибка: ' + (e instanceof Error ? e.message : String(e)))
                  } finally { setTranslating(null) }
                }}
                disabled={isBusy}
                style={{ fontSize: '10px', padding: '3px 7px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '4px', cursor: isBusy ? 'wait' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
              >
                {isBusy ? '⏳' : hasTr ? '🔄' : '✨ AI'}
              </button>
              {hasTr && (
                <button
                  onClick={async () => {
                    if (!siteId || !confirm(`Удалить перевод ${lang}?`)) return
                    await translationsApi.delete(siteId, pageSlug, lang).catch(() => null)
                    setTranslations(prev => { const n = { ...prev }; delete n[lang]; return n })
                  }}
                  style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px' }}
                >×</button>
              )}
            </div>
          )
        })}
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
          Опубликованный сайт: добавьте <code>?lang=en</code> к URL
        </div>
      </div>
    </div>
  )
}

function PageSettingsPanel() {
  const { page, updatePage } = useEditorStore()
  const [cmsCols, setCmsCols] = useState<Array<{ id: string; slug: string; name: string; schema: Array<{ name: string; type: string }> }>>([])
  useEffect(() => {
    const siteId = localStorage.getItem('bst-site-id')
    if (!siteId) return
    cmsApi.listCollections(siteId).then(cols => setCmsCols(cols as typeof cmsCols)).catch(() => null)
  }, [])

  function extractPageText() {
    const texts: string[] = []
    for (const sec of page.sections) {
      for (const block of sec.blocks) {
        const d = block.data as Record<string, unknown>
        for (const v of Object.values(d)) {
          if (typeof v === 'string' && v.length > 3) texts.push(v)
          else if (Array.isArray(v)) {
            for (const item of v) {
              if (typeof item === 'object' && item) {
                for (const sv of Object.values(item as Record<string, unknown>)) {
                  if (typeof sv === 'string' && sv.length > 3) texts.push(sv)
                }
              }
            }
          }
        }
      }
    }
    return texts.slice(0, 30).join(' | ')
  }

  return (
    <div className="properties-panel">
      <div className="properties-header">Страница</div>
      <div className="prop-group">
        <label className="prop-label">Название страницы</label>
        <input
          type="text"
          className="prop-input"
          value={page.title}
          onChange={(e) => updatePage({ title: e.target.value })}
        />
      </div>
      <div className="prop-group">
        <label className="prop-label">URL (slug)</label>
        <input
          type="text"
          className="prop-input"
          value={page.slug}
          onChange={(e) => updatePage({ slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
        />
      </div>

      <div className="prop-group" style={{ paddingTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label className="prop-label" style={{ color: 'var(--accent)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            SEO
          </label>
          <AiSeoButton
            title={page.seoTitle || page.title}
            pageContent={extractPageText()}
            onResult={(seoTitle, seoDesc) => updatePage({ seoTitle, seoDesc })}
          />
        </div>
      </div>
      <div className="prop-group">
        <label className="prop-label">SEO заголовок</label>
        <input
          type="text"
          className="prop-input"
          placeholder={page.title}
          value={page.seoTitle}
          onChange={(e) => updatePage({ seoTitle: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>{(page.seoTitle || page.title).length}/60 символов</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">Мета-описание</label>
        <textarea
          className="prop-textarea"
          rows={3}
          placeholder="Описание для поисковиков (150–160 символов)"
          value={page.seoDesc}
          onChange={(e) => updatePage({ seoDesc: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: page.seoDesc.length > 160 ? '#f87171' : 'var(--text-muted)', marginTop: '4px' }}>{page.seoDesc.length}/160 символов</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">OG Image URL</label>
        <input
          type="text"
          className="prop-input"
          placeholder="https://..."
          value={page.ogImage}
          onChange={(e) => updatePage({ ogImage: e.target.value })}
        />
      </div>
      <div className="prop-group">
        <label className="prop-label">Canonical URL</label>
        <input
          type="url"
          className="prop-input"
          placeholder="https://example.com/page"
          value={page.canonicalUrl}
          onChange={(e) => updatePage({ canonicalUrl: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Оставьте пустым — будет использован URL страницы. Укажите для предотвращения дублирования контента.</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">Индексация поисковиками</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={page.robotsIndex}
            onChange={(e) => updatePage({ robotsIndex: e.target.checked })}
            style={{ width: '14px', height: '14px', accentColor: 'var(--accent)' }}
          />
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Разрешить индексацию (index)</span>
        </label>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Снимите галочку для страниц, которые не нужны в Google (спасибо, политики, черновики)</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">Структурированные данные (JSON-LD)</label>
        <textarea
          className="prop-textarea"
          rows={5}
          style={{ fontFamily: 'monospace', fontSize: '11px' }}
          placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "..."\n}'}
          value={page.structuredData}
          onChange={(e) => updatePage({ structuredData: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Вставьте JSON-LD для Schema.org (SEO rich snippets)</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">Пароль для страницы</label>
        <input
          type="password"
          className="prop-input"
          placeholder="Оставьте пустым для открытого доступа"
          value={page.password ?? ''}
          onChange={(e) => updatePage({ password: e.target.value })}
          autoComplete="new-password"
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Сохраните страницу, чтобы применить пароль</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">CSS для этой страницы</label>
        <textarea
          className="prop-textarea"
          rows={5}
          style={{ fontFamily: 'monospace', fontSize: '11px' }}
          placeholder={'.my-class {\n  color: red;\n}'}
          value={page.pageCss}
          onChange={(e) => updatePage({ pageCss: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Стили применяются только на этой странице</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">JS для этой страницы</label>
        <textarea
          className="prop-textarea"
          rows={5}
          style={{ fontFamily: 'monospace', fontSize: '11px' }}
          placeholder={'// Ваш JavaScript-код\nconsole.log("Hello from this page")'}
          value={page.pageJs}
          onChange={(e) => updatePage({ pageJs: e.target.value })}
        />
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Скрипт выполняется при загрузке страницы</div>
      </div>
      <div className="prop-group">
        <label className="prop-label">CMS-шаблон коллекции</label>
        {cmsCols.length > 0 ? (
          <select
            className="prop-select"
            value={page.cmsTemplateFor ?? ''}
            onChange={(e) => updatePage({ cmsTemplateFor: e.target.value || undefined })}
          >
            <option value="">— не привязана —</option>
            {cmsCols.map(c => (
              <option key={c.id} value={c.slug}>{c.name} ({c.slug})</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            className="prop-input"
            placeholder="blog, portfolio, news..."
            value={page.cmsTemplateFor ?? ''}
            onChange={(e) => updatePage({ cmsTemplateFor: e.target.value || undefined })}
          />
        )}
        {page.cmsTemplateFor && (() => {
          const col = cmsCols.find(c => c.slug === page.cmsTemplateFor)
          const fields = col?.schema.map(f => f.name) ?? []
          return fields.length > 0 ? (
            <div style={{ marginTop: '6px', padding: '6px 8px', background: 'rgba(91,106,240,0.07)', border: '1px solid rgba(91,106,240,0.2)', borderRadius: '6px', fontSize: '10px', color: '#5b6af0', lineHeight: 1.6 }}>
              📋 Поля: {fields.map(f => <code key={f} style={{ background: 'rgba(91,106,240,0.12)', padding: '1px 4px', borderRadius: '3px', marginRight: '3px' }}>{`{{${f}}}`}</code>)}
            </div>
          ) : null
        })()}
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
          Выберите коллекцию — эта страница станет шаблоном для отдельных записей.
          Вставляйте поля через {'{{field}}'} прямо в тексты блоков.
        </div>
      </div>
      <PageTranslationsSection pageSlug={page.slug} pageSections={page.sections as unknown[]} />

      <div className="prop-group">
        <label className="prop-label" style={{ color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Горячие клавиши
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
          {[
            ['Ctrl+Z', 'Отменить'],
            ['Ctrl+Y', 'Повторить'],
            ['Ctrl+D', 'Дублировать блок'],
            ['Del', 'Удалить выбранное'],
            ['Esc', 'Снять выделение'],
          ].map(([key, label]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}</span>
              <kbd style={{ background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '3px', padding: '1px 5px', fontSize: '10px', color: 'var(--text-secondary)' }}>{key}</kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// VISUAL EFFECTS SECTION (new)
// ─────────────────────────────────────────────────────────────────────────────
const SHADOW_PRESETS = [
  { label: 'Нет', value: '' },
  { label: 'XS', value: '0 1px 4px rgba(0,0,0,0.08)' },
  { label: 'SM', value: '0 2px 12px rgba(0,0,0,0.10)' },
  { label: 'MD', value: '0 4px 24px rgba(0,0,0,0.12)' },
  { label: 'LG', value: '0 8px 40px rgba(0,0,0,0.16)' },
  { label: 'XL', value: '0 16px 64px rgba(0,0,0,0.20)' },
  { label: 'Glow', value: '0 0 32px rgba(91,106,240,0.45)' },
  { label: 'Inner', value: 'inset 0 2px 8px rgba(0,0,0,0.14)' },
]

const RADIUS_PRESETS = [
  { label: '0', value: '0' },
  { label: '4', value: '4px' },
  { label: '8', value: '8px' },
  { label: '12', value: '12px' },
  { label: '16', value: '16px' },
  { label: '24', value: '24px' },
  { label: '32', value: '32px' },
  { label: '∞', value: '9999px' },
]

function VisualEffectsSection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const setBlockWrapper = useEditorStore(s => s.setBlockWrapper)
  const [open, setOpen] = useState(false)
  const vs = block.blockVisualStyles ?? {}

  function set(patch: Partial<BlockVisualStyles>) {
    setBlockWrapper(sectionId, block.id, { blockVisualStyles: { ...vs, ...patch } })
  }

  const hasSomething = vs.borderRadius || vs.boxShadow || vs.opacity !== undefined || vs.backdropFilter || vs.mixBlendMode || vs.rotate || vs.scale || vs.overflow || vs.borderWidth

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left' }}
      >
        <span style={{ flex: 1, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: hasSomething ? 'var(--accent)' : 'var(--text-muted)' }}>
          ✦ Визуальные эффекты {hasSomething ? '•' : ''}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

          {/* Border radius */}
          <div>
            <label className="prop-label">Скругление углов</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
              {RADIUS_PRESETS.map(p => (
                <button key={p.value} onClick={() => set({ borderRadius: p.value || undefined })}
                  style={{ padding: '3px 7px', fontSize: '10px', borderRadius: '4px', border: '1px solid var(--border)', background: vs.borderRadius === p.value ? 'var(--accent)' : 'var(--bg-2)', color: vs.borderRadius === p.value ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                  {p.label}
                </button>
              ))}
            </div>
            <input className="prop-input" placeholder="напр. 8px 24px 0 0" value={vs.borderRadius ?? ''}
              onChange={e => set({ borderRadius: e.target.value || undefined })} style={{ fontSize: '11px' }} />
          </div>

          {/* Box shadow */}
          <div>
            <label className="prop-label">Тень (box-shadow)</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
              {SHADOW_PRESETS.map(p => (
                <button key={p.label} onClick={() => set({ boxShadow: p.value || undefined })}
                  style={{ padding: '3px 7px', fontSize: '10px', borderRadius: '4px', border: '1px solid var(--border)', background: vs.boxShadow === p.value ? 'var(--accent)' : 'var(--bg-2)', color: vs.boxShadow === p.value ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                  {p.label}
                </button>
              ))}
            </div>
            <input className="prop-input" placeholder="напр. 0 4px 24px rgba(0,0,0,0.12)" value={vs.boxShadow ?? ''}
              onChange={e => set({ boxShadow: e.target.value || undefined })} style={{ fontSize: '11px' }} />
          </div>

          {/* Opacity */}
          <div>
            <label className="prop-label">Прозрачность: {vs.opacity !== undefined ? `${Math.round((vs.opacity) * 100)}%` : '100%'}</label>
            <input type="range" min={0} max={1} step={0.01} value={vs.opacity ?? 1}
              onChange={e => set({ opacity: Number(e.target.value) === 1 ? undefined : Number(e.target.value) })}
              style={{ width: '100%' }} />
          </div>

          {/* Border */}
          <div>
            <label className="prop-label">Граница</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input className="prop-input" placeholder="толщина (1px)" value={vs.borderWidth ?? ''}
                onChange={e => set({ borderWidth: e.target.value || undefined })} style={{ flex: 1 }} />
              <select className="prop-input" value={vs.borderStyle ?? 'solid'}
                onChange={e => set({ borderStyle: e.target.value })} style={{ width: '90px' }}>
                <option value="solid">solid</option>
                <option value="dashed">dashed</option>
                <option value="dotted">dotted</option>
              </select>
              <input type="color" value={vs.borderColor ?? '#e5e7eb'}
                onChange={e => set({ borderColor: e.target.value })}
                style={{ width: '36px', height: '32px', padding: 0, border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none' }} />
            </div>
          </div>

          {/* Backdrop filter */}
          <div>
            <label className="prop-label">Backdrop blur (стекло)</label>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
              {[['Нет', ''], ['4px', 'blur(4px)'], ['8px', 'blur(8px)'], ['16px', 'blur(16px)'], ['24px', 'blur(24px)']].map(([l, v]) => (
                <button key={l} onClick={() => set({ backdropFilter: v || undefined })}
                  style={{ padding: '3px 7px', fontSize: '10px', borderRadius: '4px', border: '1px solid var(--border)', background: vs.backdropFilter === v ? 'var(--accent)' : 'var(--bg-2)', color: vs.backdropFilter === v ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Mix blend mode */}
          <div>
            <label className="prop-label">Blend mode</label>
            <select className="prop-input" value={vs.mixBlendMode ?? ''} onChange={e => set({ mixBlendMode: e.target.value || undefined })}>
              <option value="">Нет</option>
              {['multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'soft-light', 'hard-light', 'difference', 'exclusion'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Transform */}
          <div>
            <label className="prop-label">Трансформации</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>Поворот</div>
                <input className="prop-input" placeholder="напр. 3deg" value={vs.rotate ?? ''}
                  onChange={e => set({ rotate: e.target.value || undefined })} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>Масштаб</div>
                <input className="prop-input" placeholder="напр. 1.05" value={vs.scale ?? ''}
                  onChange={e => set({ scale: e.target.value || undefined })} />
              </div>
            </div>
          </div>

          {/* Overflow */}
          <div>
            <label className="prop-label">Overflow</label>
            <select className="prop-input" value={vs.overflow ?? ''} onChange={e => set({ overflow: (e.target.value as 'hidden' | 'visible' | 'auto') || undefined })}>
              <option value="">по умолчанию</option>
              <option value="hidden">hidden (обрезать)</option>
              <option value="visible">visible</option>
              <option value="auto">auto (скролл)</option>
            </select>
          </div>

          {hasSomething && (
            <button onClick={() => setBlockWrapper(sectionId, block.id, { blockVisualStyles: undefined })}
              style={{ fontSize: '11px', padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)' }}>
              Сбросить эффекты
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RESPONSIVE OVERRIDES SECTION (new)
// ─────────────────────────────────────────────────────────────────────────────
function ResponsiveOverridesSection({ block, sectionId }: { block: BlockState; sectionId: string }) {
  const setBlockWrapper = useEditorStore(s => s.setBlockWrapper)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'tablet' | 'mobile'>('mobile')
  const rs = block.blockResponsiveStyles ?? {}
  const cur = rs[tab] ?? {}

  function set(patch: Partial<typeof cur>) {
    setBlockWrapper(sectionId, block.id, {
      blockResponsiveStyles: { ...rs, [tab]: { ...cur, ...patch } }
    })
  }

  const hasOverrides = !!(rs.tablet && Object.values(rs.tablet).some(v => v !== undefined)) ||
    !!(rs.mobile && Object.values(rs.mobile).some(v => v !== undefined))

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left' }}
      >
        <span style={{ flex: 1, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: hasOverrides ? 'var(--accent)' : 'var(--text-muted)' }}>
          📱 Адаптивные переопределения {hasOverrides ? '•' : ''}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px', lineHeight: 1.5 }}>
            Задайте другие значения для планшета или мобильного. Пустые поля наследуют десктопные.
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {(['tablet', 'mobile'] as const).map(t => {
              const hasT = !!(rs[t] && Object.values(rs[t]!).some(v => v !== undefined))
              return (
                <button key={t} onClick={() => setTab(t)}
                  style={{ flex: 1, padding: '5px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', border: '1px solid var(--border)', background: tab === t ? 'var(--accent)' : 'var(--bg-2)', color: tab === t ? '#fff' : hasT ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer' }}>
                  {t === 'tablet' ? '⬜ Планшет' : '📱 Мобильный'}{hasT ? ' •' : ''}
                </button>
              )
            })}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Padding */}
            <div>
              <label className="prop-label">Отступы (px)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                {([['wrapPaddingTop', '↑ Сверху'], ['wrapPaddingBottom', '↓ Снизу'], ['wrapPaddingLeft', '← Слева'], ['wrapPaddingRight', '→ Справа']] as [keyof typeof cur, string][]).map(([k, l]) => (
                  <div key={k}>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginBottom: '2px' }}>{l}</div>
                    <input type="number" className="prop-input" min={0} max={400}
                      placeholder={String(block[k as keyof BlockState] ?? '—')}
                      value={cur[k] !== undefined ? String(cur[k]) : ''}
                      onChange={e => set({ [k]: e.target.value !== '' ? Number(e.target.value) : undefined })}
                      style={{ fontSize: '11px' }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Typography */}
            <div>
              <label className="prop-label">Размер шрифтов</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginBottom: '2px' }}>Заголовок</div>
                  <input className="prop-input" placeholder={block.blockTypography?.headingSize ?? '—'}
                    value={cur.headingSize ?? ''}
                    onChange={e => set({ headingSize: e.target.value || undefined })}
                    style={{ fontSize: '11px' }} />
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginBottom: '2px' }}>Текст</div>
                  <input className="prop-input" placeholder={block.blockTypography?.bodySize ?? '—'}
                    value={cur.bodySize ?? ''}
                    onChange={e => set({ bodySize: e.target.value || undefined })}
                    style={{ fontSize: '11px' }} />
                </div>
              </div>
            </div>

            {/* Min height */}
            <div>
              <label className="prop-label">Мин. высота (px)</label>
              <input type="number" className="prop-input" min={0} max={2000}
                placeholder={String(block.blockMinHeight ?? '—')}
                value={cur.blockMinHeight !== undefined ? String(cur.blockMinHeight) : ''}
                onChange={e => set({ blockMinHeight: e.target.value !== '' ? Number(e.target.value) : undefined })}
                style={{ fontSize: '11px' }} />
            </div>
          </div>

          {hasOverrides && (
            <button onClick={() => setBlockWrapper(sectionId, block.id, { blockResponsiveStyles: undefined })}
              style={{ marginTop: '10px', fontSize: '11px', padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)' }}>
              Сбросить все переопределения
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION LAYOUT SECTION (new — injected into SectionProperties)
// ─────────────────────────────────────────────────────────────────────────────
function SectionLayoutSection({ sectionId, layout }: { sectionId: string; layout?: SectionLayout }) {
  const updateSection = useEditorStore(s => s.updateSection)
  const [open, setOpen] = useState(false)
  const l = layout ?? {}

  function set(patch: Partial<SectionLayout>) {
    updateSection(sectionId, { sectionLayout: { ...l, ...patch } })
  }

  const isActive = l.display === 'flex' || l.display === 'grid'

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left' }}
      >
        <span style={{ flex: 1, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: isActive ? 'var(--accent)' : 'var(--text-muted)' }}>
          ⬚ Layout блоков в секции {isActive ? '•' : ''}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Изменяет расположение блоков внутри секции. По умолчанию блоки идут вертикально.
          </div>

          {/* Display mode */}
          <div>
            <label className="prop-label">Режим</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {(['block', 'flex', 'grid'] as const).map(d => (
                <button key={d} onClick={() => set({ display: d })}
                  style={{ flex: 1, padding: '5px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', border: '1px solid var(--border)', background: (l.display ?? 'block') === d ? 'var(--accent)' : 'var(--bg-2)', color: (l.display ?? 'block') === d ? '#fff' : 'var(--text-muted)', cursor: 'pointer' }}>
                  {d === 'block' ? '↕ Stack' : d === 'flex' ? '⇄ Flex' : '⊞ Grid'}
                </button>
              ))}
            </div>
          </div>

          {l.display === 'flex' && (
            <>
              <div>
                <label className="prop-label">Направление</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(['column', 'row', 'row-reverse'] as const).map(d => (
                    <button key={d} onClick={() => set({ flexDirection: d })}
                      style={{ flex: 1, padding: '4px 6px', fontSize: '10px', fontWeight: 600, borderRadius: '5px', border: '1px solid var(--border)', background: (l.flexDirection ?? 'column') === d ? 'var(--accent)' : 'var(--bg-2)', color: (l.flexDirection ?? 'column') === d ? '#fff' : 'var(--text-muted)', cursor: 'pointer' }}>
                      {d === 'column' ? '↕' : d === 'row' ? '→' : '←'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="prop-label">Перенос строк</label>
                <select className="prop-input" value={l.flexWrap ?? 'nowrap'} onChange={e => set({ flexWrap: e.target.value as 'wrap' | 'nowrap' })}>
                  <option value="nowrap">Нет (nowrap)</option>
                  <option value="wrap">Перенос (wrap)</option>
                </select>
              </div>
              <div>
                <label className="prop-label">Выравнивание (align-items)</label>
                <select className="prop-input" value={l.alignItems ?? ''} onChange={e => set({ alignItems: e.target.value || undefined })}>
                  <option value="">auto</option>
                  <option value="flex-start">flex-start (сверху)</option>
                  <option value="center">center (по центру)</option>
                  <option value="flex-end">flex-end (снизу)</option>
                  <option value="stretch">stretch (растянуть)</option>
                  <option value="baseline">baseline</option>
                </select>
              </div>
              <div>
                <label className="prop-label">Распределение (justify-content)</label>
                <select className="prop-input" value={l.justifyContent ?? ''} onChange={e => set({ justifyContent: e.target.value || undefined })}>
                  <option value="">auto</option>
                  <option value="flex-start">flex-start</option>
                  <option value="center">center</option>
                  <option value="flex-end">flex-end</option>
                  <option value="space-between">space-between</option>
                  <option value="space-around">space-around</option>
                  <option value="space-evenly">space-evenly</option>
                </select>
              </div>
            </>
          )}

          {l.display === 'grid' && (
            <div>
              <label className="prop-label">Колонки (grid-template-columns)</label>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                {[
                  ['2 равных', 'repeat(2, 1fr)'],
                  ['3 равных', 'repeat(3, 1fr)'],
                  ['4 равных', 'repeat(4, 1fr)'],
                  ['Auto-fit', 'repeat(auto-fit, minmax(280px, 1fr))'],
                  ['30/70', '30% 70%'],
                ].map(([label, val]) => (
                  <button key={label} onClick={() => set({ gridCols: val })}
                    style={{ padding: '3px 7px', fontSize: '10px', borderRadius: '4px', border: '1px solid var(--border)', background: l.gridCols === val ? 'var(--accent)' : 'var(--bg-2)', color: l.gridCols === val ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}>
                    {label}
                  </button>
                ))}
              </div>
              <input className="prop-input" placeholder="напр. repeat(3, 1fr)" value={l.gridCols ?? ''}
                onChange={e => set({ gridCols: e.target.value || undefined })} style={{ fontSize: '11px' }} />
            </div>
          )}

          {isActive && (
            <div>
              <label className="prop-label">Gap между блоками</label>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                {[['0', '0'], ['8', '8px'], ['16', '16px'], ['24', '24px'], ['32', '32px'], ['48', '48px']].map(([l2, v]) => (
                  <button key={l2} onClick={() => set({ gap: v })}
                    style={{ padding: '3px 7px', fontSize: '10px', borderRadius: '4px', border: '1px solid var(--border)', background: l.gap === v ? 'var(--accent)' : 'var(--bg-2)', color: l.gap === v ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}>
                    {l2}
                  </button>
                ))}
              </div>
              <input className="prop-input" placeholder="напр. 24px" value={l.gap ?? ''}
                onChange={e => set({ gap: e.target.value || undefined })} style={{ fontSize: '11px' }} />
            </div>
          )}

          {isActive && (
            <button onClick={() => updateSection(sectionId, { sectionLayout: undefined })}
              style={{ fontSize: '11px', padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)' }}>
              Сбросить layout
            </button>
          )}
        </div>
      )}
    </div>
  )
}
